import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useDeviceType } from '../hooks/useDeviceType';
import { generateTextStyles, generateLogoShadow } from '../utils/textStyles';
import AnimatedCounter from '../components/AnimatedCounter';
import CookieBar from '../components/CookieBar';
import FloatingContactButton from '../components/FloatingContactButton';
import { trackClick } from '../components/AnalyticsTracker';
import { fetchPublicCmsConfig } from '../utils/cmsConfigCache';
import PreviewBanner from '../components/PreviewBanner';
import { PublicSite } from '../components/public/PublicSite';
import { CmsImage } from '../components/public/CmsImage';
import { backgroundDisplayStyle, imageDisplay } from '../utils/imageDisplay';
import {
  PackageSearch,
  ShoppingCart,
  ClipboardList,
  Calendar,
  BookOpen,
  Package,
  FileText,
  Users,
  Clock,
  CalendarClock,
  Wallet,
  MessageSquare,
  Car,
  Lock,
  Settings,
  ExternalLink,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Zap,
  Shield,
  Cpu,
  BarChart3,
  Cog,
  ArrowRight,
  Menu,
  X,
  Info,
  Quote,
  Star,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import PresentationFrame from '../components/PresentationFrame';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: container width class based on block's fullWidth setting
const cw = (ct, defaultCls = 'container mx-auto') => ct?.fullWidth ? 'w-full px-4 md:px-8' : defaultCls;

// Helper: crop classes based on block's imageCrop setting
const cropClass = (ct) => {
  const map = { square: 'aspect-square', landscape: 'aspect-[4/3]', widescreen: 'aspect-[16/9]', portrait: 'aspect-[3/4]', tall: 'aspect-[2/3]' };
  return ct?.imageCrop && ct.imageCrop !== 'auto' ? map[ct.imageCrop] || '' : '';
};
const cropPos = (ct) => ({ objectPosition: ct?.imageFocalPoint || 'center' });
// Helper: returns 'object-contain' if user opted to keep whole image, else 'object-cover'
const fitClass = (ct) => (ct?.imageFit === 'contain' ? 'object-contain' : 'object-cover');
// Helper: container bg color used when imageFit=contain so empty letterboxed areas look intentional
const fitBgStyle = (ct) => (ct?.imageFit === 'contain' && ct?.imageFitBg ? { backgroundColor: ct.imageFitBg } : {});

// Per-item helper: priority item-level crop over block-level
const itemCropClass = (item, fieldKey = 'image', blockCt = null) => {
  const map = { square: 'aspect-square', landscape: 'aspect-[4/3]', widescreen: 'aspect-[16/9]', portrait: 'aspect-[3/4]', tall: 'aspect-[2/3]' };
  const crop = item?.[`${fieldKey}Crop`] || blockCt?.imageCrop;
  return crop && crop !== 'auto' ? map[crop] || '' : '';
};
const itemCropPos = (item, fieldKey = 'image', blockCt = null) => ({
  objectPosition: item?.[`${fieldKey}FocalPoint`] || blockCt?.imageFocalPoint || 'center'
});
const itemFitClass = (item, fieldKey = 'image', blockCt = null) => {
  const fit = item?.[`${fieldKey}Fit`] || blockCt?.imageFit;
  return fit === 'contain' ? 'object-contain' : 'object-cover';
};

// Helper: container props for multi-item card grids (modules, features, etc.)
// Supports layouts: grid (default), staggered, overlap, masonry
const getItemsLayoutProps = (content, { defaultCols = 3, defaultGap = 24 } = {}) => {
  const cols = content.columns || defaultCols;
  const gap = content.itemGap ?? defaultGap;
  const layout = content.itemLayout || 'grid';
  const colsMap = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-2 lg:grid-cols-3', 4: 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', 5: 'md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5' };
  const masonryColsMap = { 2: 'sm:columns-2', 3: 'sm:columns-2 lg:columns-3', 4: 'sm:columns-2 lg:columns-3 xl:columns-4', 5: 'sm:columns-2 lg:columns-3 xl:columns-5' };

  if (layout === 'masonry') {
    return {
      className: `columns-1 ${masonryColsMap[cols] || 'sm:columns-2 lg:columns-3'}`,
      style: { columnGap: `${gap}px` },
      isMasonry: true,
      gap,
    };
  }
  return {
    className: `grid grid-cols-1 ${colsMap[cols] || 'md:grid-cols-2 lg:grid-cols-3'} ${layout === 'overlap' ? 'relative' : ''}`,
    style: { gap: `${gap}px` },
    isMasonry: false,
    gap,
  };
};

// Per-item wrapper style: offset/rotate/zIndex for overlap; alternating margin for staggered
const getItemWrapperStyle = (item, content, index) => {
  const layout = content.itemLayout || 'grid';
  const style = {};
  if (layout === 'overlap') {
    const ox = item.offsetX || 0;
    const oy = item.offsetY || 0;
    const rot = item.rotate || 0;
    const parts = [];
    if (ox || oy) parts.push(`translate(${ox}px, ${oy}px)`);
    if (rot) parts.push(`rotate(${rot}deg)`);
    if (parts.length) style.transform = parts.join(' ');
    style.zIndex = item.zIndex ?? 1;
    style.position = 'relative';
  } else if (layout === 'staggered') {
    const offset = content.staggerOffset ?? 40;
    if (index % 2 === 1) style.marginTop = `${offset}px`;
  }
  return style;
};

// Build a block button href based on link type
const buildButtonHref = (content) => {
  const type = content.buttonLinkType || 'anchor';
  const val = content.buttonLink || '';
  if (!val) return '#';
  if (type === 'email' && !val.startsWith('mailto:')) return `mailto:${val}`;
  if (type === 'tel' && !val.startsWith('tel:')) return `tel:${val.replace(/\s/g, '')}`;
  return val;
};

// Shared visibility check: a block button should render only when enabled AND has text
const shouldRenderButton = (content) => {
  if (content?.buttonEnabled === false) return false;
  return !!(content?.buttonText || content?.button_text);
};

// Tailwind class for button align (block button wrapper)
const buttonAlignClass = (content) => {
  const a = content?.buttonAlign || 'center';
  return a === 'left' ? 'justify-start' : a === 'right' ? 'justify-end' : 'justify-center';
};

// Detect whether a stored text field is HTML (from WYSIWYG) or plain text (legacy)
const isHtmlContent = (s) => typeof s === 'string' && /<(p|h[1-6]|ul|ol|li|strong|em|u|span|a|br|div)\b/i.test(s);

// Inline style for block-level spacing controls (padding, line-height, letter-spacing).
// Padding values in px; omitted ones fall back to Tailwind classes (py-20 etc.).
const blockSpacingStyle = (content) => {
  const style = {};
  if (!content) return style;
  if (content.paddingTop != null) style.paddingTop = `${content.paddingTop}px`;
  if (content.paddingBottom != null) style.paddingBottom = `${content.paddingBottom}px`;
  if (content.paddingX != null) {
    style.paddingLeft = `${content.paddingX}px`;
    style.paddingRight = `${content.paddingX}px`;
  }
  if (content.lineHeight != null) style.lineHeight = content.lineHeight;
  if (content.letterSpacing != null) style.letterSpacing = `${content.letterSpacing}px`;
  return style;
};

// Strip the outer <p>...</p> wrapper(s) that Tiptap adds, so we can render
// rich text inside heading-level elements (h1/h2/h3) without nesting blocks.
const stripParagraph = (html) => {
  if (!html || typeof html !== 'string') return html;
  let s = html.trim();
  // Strip leading/trailing <h1>-<h6> wrappers (legacy data) — heading semantics
  // are already provided by the wrapping element (h1/h2/h3) in the public render.
  s = s.replace(/<h([1-6])(?:\s[^>]*)?>([\s\S]*?)<\/h\1>/gi, '$2');
  // Remove ALL empty paragraphs (with or without attributes/whitespace).
  // These come from TipTap when the user pressed Enter at the end and add
  // ~16px of default browser margin that creates a phantom gap between the
  // title and the subtitle.
  s = s.replace(/<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, '');
  // If content is exactly one paragraph, drop the <p> wrapper
  const single = s.match(/^<p(?:\s[^>]*)?>([\s\S]*?)<\/p>$/i);
  if (single) return single[1];
  // Multiple paragraphs: replace </p><p> with <br/> and strip outer p
  s = s.replace(/<\/p>\s*<p(?:\s[^>]*)?>/gi, '<br/>');
  s = s.replace(/^<p(?:\s[^>]*)?>/i, '').replace(/<\/p>\s*$/i, '');
  // Final pass: kill any remaining empty <p></p>
  s = s.replace(/<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, '');
  return s;
};

// Strip inline typography styles from rich-text HTML.
// Since the RichText toolbar no longer exposes font-size/font-family/font-weight,
// any pre-existing inline values are stale and must always be removed so the
// wrapper element's typography (Typography tab) wins.
// `color` is stripped only when the caller passes textStyles + fieldKey AND that
// field has its own color set — otherwise legacy per-word colors are preserved
// (e.g. inside item descriptions where there is no field-level color override).
const _typographyHasColor = (textStyles, fieldKey) =>
  !!(textStyles && fieldKey && textStyles[`${fieldKey}Color`]);

const stripTypographyInlineStyles = (html, stripColor = false) => {
  if (!html || typeof html !== 'string') return html;
  const propsToStrip = new Set(['font-size', 'font-family', 'font-weight']);
  if (stripColor) propsToStrip.add('color');
  return html.replace(/style="([^"]*)"/gi, (_match, css) => {
    const cleaned = css
      .split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .filter(decl => !propsToStrip.has(decl.split(':')[0].trim().toLowerCase()))
      .join('; ');
    return cleaned ? `style="${cleaned}"` : '';
  });
};

// Returns JSX rendering either raw HTML or plain text as children.
// ALWAYS strips obsolete inline font-size/font-family/font-weight so the wrapper element's
// typography (from the Typography tab) wins. Color is stripped only when Typography defines one.
const richTextProps = (value, textStyles, fieldKey) => {
  if (!value) return { children: null };
  if (isHtmlContent(value)) {
    const html = stripTypographyInlineStyles(value, _typographyHasColor(textStyles, fieldKey));
    return { dangerouslySetInnerHTML: { __html: html } };
  }
  return { children: value };
};

const richInlineProps = (value, textStyles, fieldKey) =>
  richTextProps(stripParagraph(value), textStyles, fieldKey);

// Helper to get animation CSS class from CMS animation settings (moved outside component)
const getAnimationClass = (block) => {
  if (!block?.animation?.enabled) return '';
  const animationType = block?.animation?.type || 'fade-up';
  const animationMap = {
    'fade-up': 'animate-fade-in-up',
    'fade-down': 'animate-fade-in-up',
    'slide-left': 'animate-slide-in-left',
    'slide-right': 'animate-slide-in-right',
    'scale': 'animate-scale-in',
    'bounce': 'animate-fade-in-up',
    'rotate': 'animate-scale-in',
    'none': ''
  };
  return animationMap[animationType] || 'animate-fade-in-up';
};

// Scroll-animated section component (stable reference - defined outside HomePage)
const ScrollAnimatedSection = ({ block, children, className = '', style = {}, delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1, triggerOnce: false });
  const animClass = getAnimationClass(block);
  
  if (!animClass) {
    return <div className={className} style={style}>{children}</div>;
  }
  
  return (
    <div
      ref={ref}
      className={`scroll-animate ${animClass} ${isVisible ? 'is-visible' : ''} ${className}`}
      style={{ ...style, transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

// Scroll-animated card component (stable reference - defined outside HomePage)
const ScrollAnimatedCard = ({ block, children, className = '', style = {}, index = 0 }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1, triggerOnce: false });
  const animClass = getAnimationClass(block);
  
  if (!animClass) {
    return <div className={className} style={style}>{children}</div>;
  }
  
  return (
    <div
      ref={ref}
      className={`scroll-animate ${animClass} ${isVisible ? 'is-visible' : ''} ${className}`}
      style={{ ...style, transitionDelay: `${index * 0.1}s` }}
    >
      {children}
    </div>
  );
};

// FAQ Accordion item component
const FaqItem = ({ item, blockColors, faqStyles, deviceType }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border rounded-xl overflow-hidden transition-all" style={{ borderColor: '#e2e8f0' }}>
      <button className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors" onClick={() => setOpen(!open)}>
        <span className="font-semibold text-lg pr-4" style={{...generateTextStyles(faqStyles, 'subtitle', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{item.title}</span>
        <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 leading-relaxed whitespace-pre-line rich-content" style={{color: '#475569', ...generateTextStyles(faqStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}} {...richTextProps(item.description, faqStyles, 'subtitle')} />
      )}
    </div>
  );
};

const HomePage = ({ overrideBlocks, pageTitle, sharedHeader = true, sharedFooter = true, pageNavigation = null, pageFooter = null }) => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(88);
  const navigate = useNavigate();
  const location = useLocation();
  const [siteConfig, setSiteConfig] = useState(null);
  const [cmsConfig, setCmsConfig] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const deviceType = useDeviceType();

  // On location.hash change (e.g. navigated from /gdpr to /#kontakt), scroll to target block
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Wait for DOM render / content load
    const attempt = (tries = 0) => {
      const el = document.getElementById(id);
      if (el) {
        const headerEl = document.querySelector('[data-testid="main-header"]');
        const offset = headerEl ? headerEl.offsetHeight + 16 : 96;
        const y = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        return;
      }
      if (tries < 20) setTimeout(() => attempt(tries + 1), 120);
    };
    attempt();
  }, [location.hash, cmsConfig]);

  // Sticky header scroll detection + measure header height
  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > 60);
      const header = document.querySelector('[data-testid="main-header"]');
      if (header) setHeaderHeight(header.offsetHeight);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Intercept clicks on internal links inside rich-text content so we use
  // the SPA router instead of triggering a full page reload (which on the
  // dev preview can take several seconds to re-download the bundle).
  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;
      // Skip links that already opt into a new tab / window or have a
      // download attribute — those should keep their default behaviour.
      if (a.target && a.target !== '' && a.target !== '_self') return;
      if (a.hasAttribute('download')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const href = a.getAttribute('href');
      if (!href) return;
      // Only intercept internal links: starts with `/` (but not `//`) or `#`
      if (href.startsWith('//')) return;
      if (href.startsWith('http://') || href.startsWith('https://')) return;
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return;
      if (!href.startsWith('/') && !href.startsWith('#')) return;
      e.preventDefault();
      if (href.startsWith('#')) {
        // Same-page anchor: just scroll smoothly to target
        const id = href.slice(1);
        const el = document.getElementById(id);
        if (el) {
          const headerEl = document.querySelector('[data-testid="main-header"]');
          const offset = headerEl ? headerEl.offsetHeight + 16 : 96;
          const y = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
        return;
      }
      // Internal absolute path → SPA navigate
      navigate(href);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [navigate]);

  useEffect(() => {
    const loadContent = async () => {
      try {
        // CMS is the source of truth. Legacy data must not block public pages.
        const cfg = await fetchPublicCmsConfig();
        setCmsConfig(cfg);
        axios.get(`${API}/content`).then(response => setSiteConfig(response.data)).catch(() => {});
      } catch (error) {
        setLoadError(true);
        console.error('Failed to load content:', error);
      }
    };
    loadContent();
  }, []);

  // Secret admin menu - 10x click on logo
  const handleLogoClick = () => {
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    
    if (newCount === 10) {
      navigate('/admin');
      setLogoClickCount(0);
    }
    
    // Reset after 3 seconds of inactivity
    setTimeout(() => setLogoClickCount(0), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/registrations`, formData);
      
      toast({
        title: "Registrace úspěšně odeslána!",
        description: "Brzy vás budeme kontaktovat na uvedeném emailu nebo telefonu.",
      });
      
      setFormData({ company: '', name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Chyba při odesílání",
        description: "Omlouváme se, zkuste to prosím později nebo nás kontaktujte telefonicky.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const modules = [
    {
      icon: ShoppingCart,
      title: 'Poptávky',
      subtitle: 'Požadavky a cenové nabídky',
      color: 'bg-cyan-500',
      description: 'Správa poptávek od zákazníků, tvorba cenových nabídek a sledování procesu od poptávky po realizaci.'
    },
    {
      icon: ClipboardList,
      title: 'Úkoly',
      subtitle: 'Zobrazit seznam',
      color: 'bg-green-500',
      description: 'Organizace pracovních úkolů, přiřazování odpovědností a sledování průběhu dokončení jednotlivých aktivit.'
    },
    {
      icon: Calendar,
      title: 'Kalendář',
      subtitle: 'Přehled zásahů',
      color: 'bg-blue-500',
      description: 'Denní, týdenní a měsíční přehled s viditelností obsazení techniků, plánování servisních zásahů a rezervací.'
    },
    {
      icon: BookOpen,
      title: 'Rezervace',
      subtitle: 'Online rezervace',
      color: 'bg-blue-600',
      description: 'Online rezervační systém pro klienty s automatickým plánováním dostupných termínů.'
    },
    {
      icon: Package,
      title: 'Sklad',
      subtitle: 'Skladová evidence a pohyby',
      color: 'bg-orange-500',
      description: 'Komplexní správa skladu s evidencí položek, sledováním pohybů, vyskladněním materiálu na zakázky a kontrolou minimálních zásob.'
    },
    {
      icon: FileText,
      title: 'Faktury',
      subtitle: 'Vydané a přijaté faktury',
      color: 'bg-green-600',
      description: 'Správa fakturace, generování PDF dokumentů, kontrola splatnosti a kompletní historie plateb.'
    },
    {
      icon: Users,
      title: 'Správa uživatelů',
      subtitle: 'Uživatelé a oprávnění',
      color: 'bg-sky-500',
      description: 'Komplexní systém správy uživatelů s rolemi a oprávněními přizpůsobenými specifickým potřebám vaší firmy.'
    },
    {
      icon: Clock,
      title: 'Docházka',
      subtitle: 'Pracovní doba',
      color: 'bg-red-500',
      description: 'Sledování pracovní doby zaměstnanců, evidence příchodů, odchodů, přestávek s možností záznamu z mobilního zařízení.'
    },
    {
      icon: CalendarClock,
      title: 'Směny',
      subtitle: 'Směnový kalendář',
      color: 'bg-indigo-500',
      description: 'Plánování a správa směn pro vícesměnný provoz s přehledným kalendářem a automatickými notifikacemi.'
    },
    {
      icon: Wallet,
      title: 'Výplaty',
      subtitle: 'Mzdy zaměstnanců',
      color: 'bg-green-500',
      description: 'Evidence a správa mezd, automatické výpočty na základě odpracované doby a přehled finančních toků.'
    },
    {
      icon: MessageSquare,
      title: 'Chat',
      subtitle: 'Komunikace týmu',
      color: 'bg-teal-500',
      description: 'Interní komunikační platforma pro rychlou výměnu informací mezi techniky a administrativou přímo v systému.'
    },
    {
      icon: Car,
      title: 'Vozový park',
      subtitle: 'Evidence vozidel',
      color: 'bg-slate-700',
      description: 'Správa firemních vozidel, sledování servisu, STK, pojištění a evidence jízd pro optimalizaci nákladů.'
    },
    {
      icon: Lock,
      title: 'Přístupový systém',
      subtitle: 'Řízení přístupu',
      color: 'bg-purple-500',
      description: 'Integrace s elektronickými přístupovými systémy a Face ID čtečkami přes MQTT nebo HTTP protokol.'
    }
  ];

  const features = [
    {
      icon: Zap,
      title: 'Software & Hardware',
      description: 'Komplexní řešení kombinující pokročilý software s hardwarovými prvky včetně OEE systémů pro maximální efektivitu výroby.'
    },
    {
      icon: Cpu,
      title: 'Průmyslová automatizace',
      description: 'Vývoj a implementace automatizačních řešení na zakázku přesně podle potřeb vašeho provozu a technologických procesů.'
    },
    {
      icon: BarChart3,
      title: 'OEE monitoring',
      description: 'Sledování celkové efektivity zařízení (Overall Equipment Effectiveness) v reálném čase s detailní analýtikou.'
    },
    {
      icon: Shield,
      title: 'Bezpečnost & Stabilita',
      description: 'Cloudové řešení s nejvyššími bezpečnostními standardy, pravidelné zálohy a garantovaná dostupnost služeb.'
    },
    {
      icon: Cog,
      title: 'Modulární architektura',
      description: 'Aktivujte pouze moduly, které skutečně potřebujete. Systém roste spolu s vaší firmou.'
    },
    {
      icon: Phone,
      title: 'Mobilní přístup',
      description: 'Plný přístup ze všech zařízení - PC, tablet i mobilní telefon. Práce odkudkoliv, kdykoliv.'
    }
  ];

  const clientLink = siteConfig?.client_section_url || "https://www.techdeskapp.cz";

  // Helper to get icon component from string name
  const getIcon = (iconName) => {
    // Fallback mapping for common icons
    const fallbackIcons = {
      Mail,
      Phone,
      MapPin,
      Info,
      ShoppingCart,
      ClipboardList,
      Calendar,
      BookOpen,
      Package,
      FileText,
      Users,
      Clock,
      CalendarClock,
      Wallet,
      MessageSquare,
      Car,
      Lock,
      Cog,
      Zap,
      Shield,
      Cpu,
      BarChart3
    };
    
    // Try to get from LucideIcons first (all icons), then fallback
    return LucideIcons[iconName] || fallbackIcons[iconName] || Info;
  };

  // Get the active blocks source (overrideBlocks for sub-pages, config blocks for home)
  const activeBlocks = overrideBlocks || cmsConfig?.blocks || [];

  // Get blocks from CMS
  const getBlock = (blockType) => {
    return activeBlocks.find(b => b.type === blockType && b.visible !== false) || null;
  };

  // Get all visible blocks sorted by order
  const getSortedBlocks = () => {
    let blocks = activeBlocks
      .filter(b => b.visible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    
    // For sub-pages: handle footer based on page-level overrides + sharedFooter
    if (overrideBlocks) {
      if (pageFooter) {
        // Page has custom footer content — inject synthetic footer block with it.
        const existingFooterIdx = blocks.findIndex(b => b.type === 'footer');
        const customFooterBlock = {
          id: `${overrideBlocks?.[0]?.id || 'page'}-custom-footer`,
          type: 'footer',
          content: pageFooter,
          visible: true,
        };
        if (existingFooterIdx >= 0) {
          blocks = blocks.map((b, i) => i === existingFooterIdx ? customFooterBlock : b);
        } else {
          blocks = [...blocks, customFooterBlock];
        }
      } else if (sharedFooter) {
        // Use global footer block if not already present
        const mainFooter = cmsConfig?.blocks?.find(b => b.type === 'footer' && b.visible !== false);
        if (mainFooter && !blocks.some(b => b.type === 'footer')) {
          blocks = [...blocks, mainFooter];
        }
      }
    }
    
    return blocks;
  };


  // Get modules from CMS or fallback to hardcoded
  const cmsModulesBlock = getBlock('modules');
  const cmsModules = cmsModulesBlock?.content?.items || modules;
  
  // Get features from CMS or fallback to hardcoded
  const cmsFeaturesBlock = getBlock('features');
  const cmsFeatures = cmsFeaturesBlock?.content?.items || features;

  // Get other blocks from CMS
  const cmsHeroBlock = getBlock('hero');
  const cmsStatsBlock = getBlock('stats');
  const cmsVersionsBlock = getBlock('versions');
  const cmsContactBlock = getBlock('contact');
  const cmsFooterBlock = getBlock('footer');
  const footerContent = cmsFooterBlock?.content || {};

  // Default/Fallback values
  const hero = siteConfig?.hero || {
    title: "Kompletní podnikový systém",
    subtitle: "pro moderní firmy",
    description: "TechDesk propojuje řízení servisních zásahů, správu zákazníků, skladové hospodářství, docházku zaměstnanců, fakturaci i plánování práce.",
    button_text: "Vyzkoušet zdarma"
  };

  // Get sorted blocks for dynamic rendering
  const sortedBlocks = getSortedBlocks();
  // Find global parallax block (if any) — it becomes a page-wide background and is skipped in rendering
  const globalParallaxBlock = sortedBlocks.find(b => b.type === 'parallax' && b?.content?.globalParallax && b?.content?.backgroundImage);

  // Block renderer - renders the appropriate component based on block type
  const renderBlock = (block) => {
    const blockContent = block.content || {};
    // If block has a backgroundImage (from Obrázek tab) or parallax, suppress internal section bg
    const hasBlockBgImage = !!(block.backgroundImage || blockContent.parallaxImage);
    // Individual style colors (only when styleMode is 'individual')
    const isIndividual = block.styleMode === 'individual';
    // If global parallax is active, default all block backgrounds to transparent unless user set one explicitly
    // Footer is excluded — it always keeps its own background (parallax doesn't show through)
    const globalParallaxActive = !!globalParallaxBlock;
    const transparentDefault = globalParallaxActive && !(isIndividual && block.backgroundColor) && !hasBlockBgImage && block.type !== 'footer';
    const blockColors = {
      bg: hasBlockBgImage ? 'transparent' : (transparentDefault ? 'transparent' : (isIndividual && block.backgroundColor ? block.backgroundColor : undefined)),
      icon: isIndividual && block.iconColor ? block.iconColor : undefined,
      iconBg: isIndividual && block.iconBgColor ? block.iconBgColor : undefined,
      title: isIndividual && block.titleColor ? block.titleColor : undefined,
      subtitle: isIndividual && block.subtitleColor ? block.subtitleColor : undefined,
      content: isIndividual && block.contentColor ? block.contentColor : undefined,
    };
    
    switch (block.type) {
      case 'hero':
        const textStyles = blockContent.textStyles || {};
        const logoShadow = generateLogoShadow(textStyles);
        const logoSize = textStyles.logoSize ? `${textStyles.logoSize}px` : '10rem';
        // Auto-detect: if user uploaded backgroundImage, use image mode; if videoUrl, video mode; else gradient
        let heroBgType = blockContent.backgroundType || 'gradient';
        if (blockContent.backgroundImage && heroBgType === 'gradient') heroBgType = 'image';
        if (blockContent.videoUrl && heroBgType === 'gradient') heroBgType = 'video';
        const heroOverlayColor = blockContent.overlayColor || '#000000';
        const heroOverlayOpacity = (blockContent.overlayOpacity ?? 40) / 100;
        const heroFullscreen = blockContent.fullscreen;
        
        const getVideoEmbedUrl = (url) => {
          if (!url) return null;
          const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
          if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${ytMatch[1]}&controls=0&showinfo=0&rel=0`;
          const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
          if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1&background=1`;
          return null;
        };

        return (
          <section 
            key={block.id} 
            id={block.anchorId || block.id} 
            className={`relative overflow-hidden ${heroFullscreen ? 'min-h-screen flex items-center' : 'py-20 px-4'}`}
          >
            {/* Gradient background (default) */}
            {heroBgType === 'gradient' && (
              <div className="absolute inset-0 z-0 pointer-events-none">
                <div className="absolute -top-20 -left-20 w-[700px] h-[700px] bg-gradient-to-br from-violet-300 to-fuchsia-200 rounded-full filter blur-[90px] opacity-25 animate-blob"></div>
                <div className="absolute -top-10 -right-20 w-[650px] h-[650px] bg-gradient-to-br from-violet-200 to-purple-200 rounded-full filter blur-[90px] opacity-25 animate-blob animation-delay-2000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-teal-200 to-violet-200 rounded-full filter blur-[100px] opacity-20 animate-blob animation-delay-4000"></div>
              </div>
            )}

            {/* Image background with opacity */}
            {heroBgType === 'image' && blockContent.backgroundImage && (
              <div className="absolute inset-0 z-0" style={{
                backgroundImage: `url("${blockContent.backgroundImage}")`,
                ...backgroundDisplayStyle(blockContent, 'backgroundImage', deviceType),
                backgroundAttachment: blockContent.parallax ? 'fixed' : 'scroll',
                backgroundRepeat: 'no-repeat',
                opacity: (blockContent.backgroundImageOpacity ?? 100) / 100
              }} />
            )}

            {/* Video background */}
            {heroBgType === 'video' && blockContent.videoUrl && (
              <>
                {blockContent.videoUrl.match(/\.mp4/i) ? (
                  <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover z-0">
                    <source src={blockContent.videoUrl} type="video/mp4" />
                  </video>
                ) : getVideoEmbedUrl(blockContent.videoUrl) ? (
                  <iframe src={getVideoEmbedUrl(blockContent.videoUrl)} className="absolute inset-0 w-full h-full z-0 pointer-events-none" style={{ transform: 'scale(1.2)' }} frameBorder="0" allow="autoplay; fullscreen" title="bg video" />
                ) : null}
              </>
            )}

            {/* Overlay for image/video */}
            {(heroBgType === 'image' || heroBgType === 'video') && heroOverlayOpacity > 0 && (
              <div className="absolute inset-0 z-[1]" style={{ backgroundColor: heroOverlayColor, opacity: heroOverlayOpacity }} />
            )}

            <ScrollAnimatedSection block={block} className={`container mx-auto text-center relative z-10 ${heroFullscreen ? 'px-4' : ''}`}>
              {blockContent.showLogo !== false && blockContent.logoUrl && (
                <div className="mb-8">
                  <CmsImage owner={blockContent} field="logoUrl"
                    src={blockContent.logoUrl}
                    alt="Logo" 
                    className="w-auto mx-auto mb-6 object-contain"
                    style={{ height: logoSize, ...logoShadow }}
                  />
                </div>
              )}
              <h1 
                className={`text-5xl md:text-6xl font-bold mb-6 leading-tight rich-content flex flex-col gap-3 ${(heroBgType === 'image' || heroBgType === 'video') ? 'text-white' : 'text-slate-900'}`}
              >
                <span 
                  className="rich-content"
                  style={generateTextStyles(textStyles, 'title', deviceType)}
                  {...richInlineProps(blockContent.title, textStyles, 'title')}
                />
                {(blockContent.titleHighlight || blockContent.subtitle) && (
                  <span 
                    className={`rich-content ${(heroBgType === 'image' || heroBgType === 'video') ? 'text-white/90' : 'text-blue-600'}`}
                    style={generateTextStyles(textStyles, 'subtitle', deviceType)}
                    {...richInlineProps(blockContent.titleHighlight || blockContent.subtitle, textStyles, 'subtitle')}
                  />
                )}
              </h1>
              {blockContent.description && (
                <div 
                  className={`text-2xl font-medium mt-2 mb-8 whitespace-pre-line rich-content ${(heroBgType === 'image' || heroBgType === 'video') ? 'text-white/80' : 'text-slate-700'}`}
                  style={generateTextStyles(textStyles, 'description', deviceType)}
                  {...richTextProps(blockContent.description, textStyles, 'description')}
                />
              )}
              {shouldRenderButton(blockContent) && (() => {
                const btnType = blockContent.buttonLinkType || 'anchor';
                const btnHref = buildButtonHref(blockContent);
                const btnTarget = blockContent.buttonNewTab || btnType === 'external' ? '_blank' : undefined;
                const btnRel = btnTarget === '_blank' ? 'noopener noreferrer' : undefined;
                const alignCls = buttonAlignClass(blockContent);
                return (
                  <div className={`flex ${alignCls}`}>
                    <a href={btnHref} target={btnTarget} rel={btnRel}>
                      <Button 
                        size="lg" 
                        className="text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
                        style={{ backgroundColor: 'var(--theme-primary)', ...generateTextStyles(textStyles, 'button', deviceType) }}
                      >
                        {blockContent.button_text || blockContent.buttonText}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </a>
                  </div>
                );
              })()}
            </ScrollAnimatedSection>
          </section>
        );

      case 'stats':
        const statsStyles = blockContent.textStyles || {};
        const statsItems = blockContent.items || [
          { value: "15+", label: "Modulů systému" },
          { value: "2", label: "Způsoby nasazení" },
          { value: "24/7", label: "Dostupnost" },
          { value: "∞", label: "Možnosti integrace" }
        ];
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-16" style={blockColors.bg ? { backgroundColor: blockColors.bg } : { backgroundColor: 'white' }}>
            <ScrollAnimatedSection block={block} className={`${cw(blockContent)} px-4`}>
              <div className="text-center mb-8">
                {blockContent.title && (
                  <h2 
                    className="text-3xl font-bold text-slate-900 mb-2"
                    style={{...generateTextStyles(statsStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}
                  >
                    {blockContent.title}
                  </h2>
                )}
                {blockContent.subtitle && (
                  <p 
                    className="text-lg text-slate-600"
                    style={{...generateTextStyles(statsStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}
                  >
                    {blockContent.subtitle}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                {statsItems.map((item, index) => (
                  <div key={item.id || index} className="p-6">
                    <div 
                      className="text-4xl font-bold text-blue-600 mb-2"
                      style={{...generateTextStyles(statsStyles, 'title', deviceType), ...(blockColors.icon ? {color: blockColors.icon} : {})}}
                    >
                      <AnimatedCounter value={item.value} duration={1800 + index * 200} />
                    </div>
                    <div 
                      className="text-slate-600 font-medium"
                      style={{...generateTextStyles(statsStyles, 'description', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}
                    >
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollAnimatedSection>
          </section>
        );

      case 'versions': {
        const versionsStyles = blockContent.textStyles || {};
        // Default cards preserve original "Cloud vs On-premise" layout for legacy configs
        const defaultCards = [
          {
            id: 'v-cloud',
            title: 'Cloudové řešení',
            subtitle: 'Bez starostí o infrastrukturu',
            icon: 'Cloud',
            iconColor: '#ffffff',
            iconBgFrom: '#3b82f6',
            iconBgTo: '#2563eb',
            borderColor: '#bfdbfe',
            featureItems: [
              { id: 'f1', icon: 'CheckCircle2', color: '#2563eb', text: 'Okamžitý start - žádná instalace' },
              { id: 'f2', icon: 'CheckCircle2', color: '#2563eb', text: 'Automatické aktualizace a zálohy' },
              { id: 'f3', icon: 'CheckCircle2', color: '#2563eb', text: 'Přístup odkudkoliv přes internet' },
              { id: 'f4', icon: 'CheckCircle2', color: '#2563eb', text: 'Garantovaná dostupnost 99,9%' },
              { id: 'f5', icon: 'CheckCircle2', color: '#2563eb', text: 'Škálovatelnost podle potřeb' },
              { id: 'f6', icon: 'CheckCircle2', color: '#2563eb', text: 'Nižší počáteční investice' },
            ],
            idealForLabel: 'Ideální pro:',
            idealFor: 'Menší a střední firmy, rozptýlené týmy, rychlý start',
          },
          {
            id: 'v-onprem',
            title: 'Vlastní server',
            subtitle: 'Nasazení do firemní sítě',
            icon: 'Server',
            iconColor: '#ffffff',
            iconBgFrom: '#6366f1',
            iconBgTo: '#4f46e5',
            borderColor: '#c7d2fe',
            featureItems: [
              { id: 'g1', icon: 'CheckCircle2', color: '#4f46e5', text: '100% kontrola nad daty' },
              { id: 'g2', icon: 'CheckCircle2', color: '#4f46e5', text: 'Integrace s interními systémy' },
              { id: 'g3', icon: 'CheckCircle2', color: '#4f46e5', text: 'Nezávislost na internetu' },
              { id: 'g4', icon: 'CheckCircle2', color: '#4f46e5', text: 'Přizpůsobení firemním standardům' },
              { id: 'g5', icon: 'CheckCircle2', color: '#4f46e5', text: 'Maximální bezpečnost a soukromí' },
              { id: 'g6', icon: 'CheckCircle2', color: '#4f46e5', text: 'Vlastní HW integrace (OEE, IoT)' },
            ],
            idealForLabel: 'Ideální pro:',
            idealFor: 'Větší firmy, citlivá data, specifické požadavky na integraci',
          },
        ];
        const versionCards = (blockContent.cards && blockContent.cards.length > 0) ? blockContent.cards : defaultCards;
        const ctaEnabled = blockContent.ctaEnabled !== false;
        const ctaText = blockContent.ctaText || 'Nevíte, které řešení je pro vás vhodné? <a href="#registrace" style="color:#2563eb;font-weight:600">Kontaktujte nás</a> a společně najdeme optimální variantu pro vaši firmu.';
        const colsClass = versionCards.length === 1 ? 'md:grid-cols-1 max-w-2xl' :
                          versionCards.length === 2 ? 'md:grid-cols-2' :
                          versionCards.length === 3 ? 'md:grid-cols-3' :
                          'md:grid-cols-2 lg:grid-cols-4';
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : { background: 'linear-gradient(to bottom right, #f8fafc, #eff6ff)' }}>
            <div className={cw(blockContent)}>
              <div className="text-center mb-16">
                {blockContent.title ? (
                  <h2 
                    className="text-4xl font-bold text-slate-900 mb-4"
                    style={{...generateTextStyles(versionsStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}
                  >
                    {blockContent.title}
                  </h2>
                ) : null}
                {blockContent.subtitle ? (
                  <p 
                    className="text-xl text-slate-600 max-w-3xl mx-auto"
                    style={{...generateTextStyles(versionsStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}
                  >
                    {blockContent.subtitle}
                  </p>
                ) : null}
              </div>
              <div className={`grid ${colsClass} gap-8 ${blockContent.fullWidth ? 'px-0' : 'max-w-7xl mx-auto px-4'}`}>
                {versionCards.map((card, idx) => {
                  const Icon = getIcon(card.icon || 'Cloud');
                  // Prefer new structured featureItems; fall back to legacy `features` (newline-separated string)
                  let featureRows = Array.isArray(card.featureItems) ? card.featureItems : null;
                  if (!featureRows) {
                    const lines = String(card.features || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
                    featureRows = lines.map((text, i) => ({ id: `_${i}`, icon: 'CheckCircle2', color: card.checkColor || '#2563eb', text }));
                  }
                  return (
                    <ScrollAnimatedCard key={card.id || idx} block={block} index={idx}>
                      <Card className="hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white h-full" style={{ borderWidth: 2, borderColor: card.borderColor || '#bfdbfe' }}>
                        <CardHeader className="pb-4">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg"
                            style={{ backgroundImage: `linear-gradient(to bottom right, ${card.iconBgFrom || '#3b82f6'}, ${card.iconBgTo || '#2563eb'})` }}
                          >
                            <Icon className="h-8 w-8" style={{ color: card.iconColor || '#ffffff' }} />
                          </div>
                          <CardTitle className="text-2xl" style={{...generateTextStyles(versionsStyles, 'subtitle', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{card.title}</CardTitle>
                          {card.subtitle && <CardDescription className="text-base" style={{...generateTextStyles(versionsStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}>{card.subtitle}</CardDescription>}
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {featureRows.length > 0 && (
                            <ul className="space-y-3">
                              {featureRows.map((row, i) => {
                                const FIcon = getIcon(row.icon || 'CheckCircle2');
                                const fColor = row.color || card.checkColor || '#2563eb';
                                return (
                                  <li key={row.id || i} className="flex items-start gap-3">
                                    <FIcon className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: fColor }} />
                                    <span style={{color: '#334155', ...generateTextStyles(versionsStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{row.text}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          {(card.idealFor || card.idealForLabel) && (
                            <div className="pt-4 border-t border-slate-200">
                              {card.idealForLabel && <p className="text-sm font-medium" style={{color: '#475569', ...generateTextStyles(versionsStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{card.idealForLabel}</p>}
                              {card.idealFor && <p className="text-sm mt-1" style={{color: '#64748b', ...generateTextStyles(versionsStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{card.idealFor}</p>}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </ScrollAnimatedCard>
                  );
                })}
              </div>
              {ctaEnabled && ctaText && (
                <div className="text-center mt-12">
                  <p className="text-lg text-slate-600 max-w-2xl mx-auto rich-content" {...richInlineProps(ctaText)} />
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'features': {
        const featuresStyles = blockContent.textStyles || {};
        const featureItems = blockContent.items || cmsFeatures;
        const fLayout = getItemsLayoutProps(blockContent, { defaultCols: 3, defaultGap: 32 });
        const fRadius = blockContent.itemRadius ?? 12;
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            <div className={cw(blockContent)}>
              <div className="text-center mb-16">
                {blockContent.title ? (
                  <h2 
                    className="text-4xl font-bold text-slate-900 mb-4"
                    style={{...generateTextStyles(featuresStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}
                  >
                    {blockContent.title}
                  </h2>
                ) : null}
                {blockContent.subtitle ? (
                  <p 
                    className="text-xl text-slate-600 max-w-2xl mx-auto"
                    style={{...generateTextStyles(featuresStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}
                  >
                    {blockContent.subtitle}
                  </p>
                ) : null}
              </div>
              <div className={fLayout.className} style={fLayout.style}>
                {featureItems.map((feature, index) => {
                  const resolvedIcon = typeof feature.icon === 'string' ? getIcon(feature.icon) : feature.icon;
                  const FeatureIcon = resolvedIcon || LucideIcons.Star;
                  const wrapperStyle = getItemWrapperStyle(feature, blockContent, index);
                  const masonryCls = fLayout.isMasonry ? 'break-inside-avoid' : '';
                  const masonryStyle = fLayout.isMasonry ? { marginBottom: `${fLayout.gap}px` } : {};
                  const featureCard = (
                      <Card className="border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full" style={{ borderRadius: `${fRadius}px` }}>
                        <CardHeader>
                          <div 
                            className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                            style={{ backgroundColor: blockColors.iconBg || (feature.iconColor ? `${feature.iconColor}20` : '#dbeafe') }}
                          >
                            {feature.image ? (
                              <CmsImage owner={feature} src={feature.image} alt={feature.title} className="h-8 w-8 object-contain" style={{ opacity: (feature.imageOpacity ?? 100) / 100 }} />
                            ) : (
                              <FeatureIcon className="h-7 w-7" style={{ color: blockColors.icon || feature.iconColor || '#2563eb' }} />
                            )}
                          </div>
                          <CardTitle className="text-xl" style={{...generateTextStyles(featuresStyles, 'subtitle', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{feature.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="leading-relaxed whitespace-pre-line rich-content" style={{color: '#475569', ...generateTextStyles(featuresStyles, 'description', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}} {...richTextProps(feature.description, featuresStyles, 'description')} />
                        </CardContent>
                      </Card>
                  );
                  return (
                    <ScrollAnimatedCard key={feature.id || index} block={block} index={index} className={masonryCls} style={{ ...wrapperStyle, ...masonryStyle }}>
                      {feature.link ? (
                        <a href={feature.link} className="block h-full" style={{ textDecoration: 'none', color: 'inherit' }} onClick={() => trackClick && trackClick({ kind: 'solution', label: feature.title, url: feature.link })}>{featureCard}</a>
                      ) : featureCard}
                    </ScrollAnimatedCard>
                  );
                })}
              </div>
            </div>
          </section>
        );
      }

      case 'modules': {
        const modulesStyles = blockContent.textStyles || {};
        // Respect admin-defined order only
        const moduleItems = (blockContent.items || cmsModules).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const mLayout = getItemsLayoutProps(blockContent, { defaultCols: 4, defaultGap: 24 });
        const mRadius = blockContent.itemRadius ?? 12;
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : { backgroundColor: 'white' }}>
            <div className={cw(blockContent)}>
              <div className="text-center mb-16">
                <h2 
                  className="text-4xl font-bold text-slate-900 mb-4"
                  style={{...generateTextStyles(modulesStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}
                >
                  {blockContent.title || ''}
                </h2>
                {blockContent.subtitle ? (
                  <p 
                    className="text-xl text-slate-600 max-w-2xl mx-auto"
                    style={{...generateTextStyles(modulesStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}
                  >
                    {blockContent.subtitle}
                  </p>
                ) : null}
              </div>
              <div className={mLayout.className} style={mLayout.style}>
                {moduleItems.map((module, index) => {
                  const ModuleIcon = typeof module.icon === 'string' ? getIcon(module.icon) : module.icon;
                  const bgColor = module.iconBgColor || (module.color?.replace('bg-', '') ? undefined : 'var(--theme-primary)');
                  const bgClass = !module.iconBgColor && module.color ? module.color : '';
                  const wrapperStyle = getItemWrapperStyle(module, blockContent, index);
                  const masonryCls = mLayout.isMasonry ? 'break-inside-avoid' : '';
                  const masonryStyle = mLayout.isMasonry ? { marginBottom: `${mLayout.gap}px` } : {};
                  return (
                    <ScrollAnimatedCard key={module.id || index} block={block} index={index} className={masonryCls} style={{ ...wrapperStyle, ...masonryStyle }}>
                      <Card className="border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group h-full relative" style={{ borderRadius: `${mRadius}px` }}>
                        {module.isNew && (
                          <div className="absolute -top-2 -right-2 z-10">
                            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                              Novinka
                            </span>
                          </div>
                        )}
                        <CardHeader>
                          <div 
                            className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform ${bgClass}`}
                            style={bgColor ? { backgroundColor: bgColor } : {}}
                          >
                            {module.image ? (
                              <CmsImage owner={module} src={module.image} alt={module.title} className="h-6 w-6 object-contain" style={{ opacity: (module.imageOpacity ?? 100) / 100 }} />
                            ) : (
                              <ModuleIcon className="h-6 w-6 text-white" />
                            )}
                          </div>
                          <CardTitle className="text-lg" style={{...generateTextStyles(modulesStyles, 'subtitle', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{module.title}</CardTitle>
                          <CardDescription style={{...generateTextStyles(modulesStyles, 'description', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}>{module.subtitle}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="text-sm leading-relaxed whitespace-pre-line rich-content" style={{color: '#475569', ...generateTextStyles(modulesStyles, 'description', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}} {...richTextProps(module.description, modulesStyles, 'description')} />
                        </CardContent>
                      </Card>
                    </ScrollAnimatedCard>
                  );
                })}
              </div>
            </div>
          </section>
        );
      }

      case 'testimonials':
        const testimonialsStyles = blockContent.textStyles || {};
        const testimonialItems = blockContent.items || [];
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : { background: 'linear-gradient(to bottom right, #f8fafc, #eff6ff)' }}>
            <ScrollAnimatedSection block={block} className={cw(blockContent)}>
              <div className="text-center mb-16">
                <h2
                  className="text-4xl font-bold text-slate-900 mb-4"
                  style={{...generateTextStyles(testimonialsStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}
                >
                  {blockContent.title || 'Co rikaji nasi klienti'}
                </h2>
                <p
                  className="text-xl text-slate-600 max-w-2xl mx-auto"
                  style={{...generateTextStyles(testimonialsStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}
                >
                  {blockContent.subtitle || ''}
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {testimonialItems.map((item, index) => (
                  <ScrollAnimatedCard key={item.id || index} block={block} index={index}>
                    <div className="testimonial-card bg-white rounded-2xl p-6 border border-slate-200 h-full flex flex-col">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(item.rating || 5)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      <Quote className="h-8 w-8 text-blue-200 mb-3" />
                      <div className="leading-relaxed flex-1 text-sm whitespace-pre-line rich-content" style={{color: '#334155', ...generateTextStyles(testimonialsStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}} {...richTextProps(item.text ? (isHtmlContent(item.text) ? item.text : `"${item.text}"`) : '', testimonialsStyles, 'subtitle')} />
                      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-slate-100">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-sm">
                          {(item.author || 'A')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{item.author || ''}</p>
                          <p className="text-xs text-slate-500">{item.company || ''}</p>
                        </div>
                      </div>
                    </div>
                  </ScrollAnimatedCard>
                ))}
              </div>
            </ScrollAnimatedSection>
          </section>
        );

      case 'contact': {
        const contactStyles = blockContent.textStyles || {};
        // Default form fields preserve backwards compatibility with the original layout
        const defaultFormFields = [
          { id: 'name', name: 'name', label: 'Vaše jméno', type: 'text', required: true, placeholder: 'Vaše jméno' },
          { id: 'email', name: 'email', label: 'E-mail', type: 'email', required: true, placeholder: 'E-mail' },
          { id: 'phone', name: 'phone', label: 'Telefon', type: 'tel', required: true, placeholder: 'Telefon' },
          { id: 'company', name: 'company', label: 'Název firmy', type: 'text', required: true, placeholder: 'Název firmy' },
          { id: 'message', name: 'message', label: 'Vaše zpráva nebo dotaz', type: 'textarea', required: false, placeholder: 'Vaše zpráva nebo dotaz' },
        ];
        const formFields = (blockContent.formFields && blockContent.formFields.length > 0) ? blockContent.formFields : defaultFormFields;
        const formTitle = blockContent.formTitle || 'Napište nám';
        const formButtonText = blockContent.buttonText || 'Odeslat zprávu';
        const infoTitle = blockContent.infoTitle || 'Další informace';
        const infoText = blockContent.infoText || 'Máte otázky k funkcím nebo cenám? Rádi vám poskytneme detailní informace a ukážeme, jak může TechDesk zefektivnit provoz vaší firmy.';
        const calloutEnabled = blockContent.calloutEnabled !== false;
        const infoColumnEnabled = blockContent.infoColumnEnabled !== false;
        const calloutTitle = blockContent.calloutTitle || 'Bezplatná prezentace';
        const calloutText = blockContent.calloutText || 'Domluvte si s námi online prezentaci systému a zjistěte, jak TechDesk může fungovat pro vaši firmu.';
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : { backgroundColor: 'white' }}>
            <ScrollAnimatedSection block={block} className={cw(blockContent, 'container mx-auto max-w-6xl')}>
              <div className="text-center mb-12">
                {blockContent.title ? (
                  <h2 
                    className="text-4xl font-bold text-slate-900 mb-4"
                    style={{...generateTextStyles(contactStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}
                  >
                    {blockContent.title}
                  </h2>
                ) : null}
                {blockContent.subtitle ? (
                  <p 
                    className="text-lg text-slate-600"
                    style={{...generateTextStyles(contactStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}
                  >
                    {blockContent.subtitle}
                  </p>
                ) : null}
              </div>
              <div className={infoColumnEnabled ? "grid md:grid-cols-2 gap-8" : "max-w-2xl mx-auto"}>
                <Card className="border-slate-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl">{formTitle}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      {formFields.map((field) => {
                        const fname = field.name || field.id;
                        const fLabel = field.label || fname;
                        const fPlaceholder = field.placeholder || fLabel;
                        const fRequired = !!field.required;
                        const fValue = formData[fname] ?? '';
                        const onF = (e) => setFormData({ ...formData, [fname]: e.target.value });
                        if (field.type === 'textarea') {
                          return <Textarea key={field.id} id={fname} value={fValue} onChange={onF} required={fRequired} className="min-h-[120px]" placeholder={fPlaceholder + (fRequired ? ' *' : '')} />;
                        }
                        return <Input key={field.id} id={fname} type={field.type || 'text'} value={fValue} onChange={onF} required={fRequired} className="h-12" placeholder={fPlaceholder + (fRequired ? ' *' : '')} />;
                      })}
                      <Button type="submit" size="lg" className="w-full text-white h-12 text-base font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-50" style={{ backgroundColor: 'var(--theme-primary)' }} disabled={isSubmitting}>
                        {isSubmitting ? 'Odesílám...' : formButtonText}
                        <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </Button>
                    </form>
                  </CardContent>
                </Card>
                <div className="space-y-6" style={{ display: infoColumnEnabled ? undefined : 'none' }}>
                  <div>
                    <h3 className="text-2xl font-bold mb-4 rich-content" style={{color: '#0f172a', ...generateTextStyles(contactStyles, 'subtitle', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}} {...richInlineProps(infoTitle, contactStyles, 'subtitle')} />
                    <div className="leading-relaxed mb-6 rich-content" style={{color: '#475569', ...generateTextStyles(contactStyles, 'description', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}} {...richTextProps(infoText, contactStyles, 'description')} />
                  </div>
                  {(blockContent.infoItems || siteConfig?.contacts || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((contact) => {
                    const Icon = getIcon(contact.icon);
                    const cType = contact.contactType || (contact.value?.includes('@') ? 'email' : contact.value?.match(/^[+\d\s()-]+$/) ? 'phone' : 'text');
                    const popisek = contact.type && contact.type !== 'email' && contact.type !== 'phone' && contact.type !== 'text' ? contact.type : '';
                    return (
                      <Card key={contact.id} className="border-slate-200 bg-slate-50" data-testid={`contact-info-${cType}`}>
                        <CardContent className="p-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: blockColors.iconBg || '#e0e7ff' }}>
                              <Icon className="h-6 w-6" style={{ color: blockColors.icon || '#4f46e5' }} />
                            </div>
                            <div>
                              {popisek ? (
                                <div className="font-semibold text-slate-900 mb-1">{popisek}</div>
                              ) : contact.label ? (
                                <div className="font-semibold text-slate-900 mb-1">{contact.label}</div>
                              ) : null}
                              {cType === 'email' ? (
                                <a href={`mailto:${contact.value}`} className="text-slate-600 hover:text-indigo-600 transition-colors">{contact.value}</a>
                              ) : cType === 'phone' ? (
                                <a href={`tel:${contact.value}`} className="text-slate-600 hover:text-indigo-600 transition-colors">{contact.value}</a>
                              ) : (
                                <span className="text-slate-600">{contact.value}</span>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {calloutEnabled && (calloutTitle || calloutText) && (
                    <Card className="border-indigo-200 bg-indigo-50">
                      <CardContent className="p-6">
                        <h4 className="font-bold text-lg mb-2 rich-content" style={{color: '#0f172a', ...generateTextStyles(contactStyles, 'subtitle', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}} {...richInlineProps(calloutTitle, contactStyles, 'subtitle')} />
                        <div className="text-sm leading-relaxed rich-content" style={{color: '#334155', ...generateTextStyles(contactStyles, 'description', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}} {...richTextProps(calloutText, contactStyles, 'description')} />
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </ScrollAnimatedSection>
          </section>
        );
      }

      case 'partners':
        const pLogos = blockContent.logos || [];
        const pDirection = blockContent.direction || 'left';
        const pHeight = blockContent.stripHeight || 80;
        const pSpeed = blockContent.speed || 30;
        const pGap = blockContent.logoGap || 60;
        const pBg = blockColors.bg || blockContent.bgColor || '#f8fafc';
        const pPause = blockContent.pauseOnHover !== false;
        const pGray = blockContent.grayscale || false;
        const pStyles = blockContent.textStyles || {};
        
        const pSticky = blockContent.sticky || false;
        const pStickyPos = blockContent.stickyPosition || 'bottom';
        const normLogo = (u) => {
          const m = String(u || '').match(/(\/api\/cms\/images\/[^?#]+)/);
          return m ? `${BACKEND_URL}${m[1]}?w=720` : (u || '');
        };
        
        return (
          <section
            key={block.id}
            id={block.anchorId || block.id}
            className={`px-4 ${pSticky ? 'fixed left-0 right-0 z-40 shadow-lg py-4 overflow-hidden' : 'py-12 overflow-hidden'}`}
            style={{
              backgroundColor: pBg,
              ...(pSticky && pStickyPos === 'top' ? { top: `${headerHeight}px`, transition: 'top 0.3s ease' } : {}),
              ...(pSticky && pStickyPos === 'bottom' ? { bottom: 0 } : {}),
            }}
            data-testid="partners-block"
          >
            {(blockContent.title || blockContent.subtitle) && !pSticky && (
              <div className={`${cw(blockContent)} text-center mb-8`}>
                {blockContent.title && (
                  <h2
                    className="text-3xl font-bold mb-2"
                    style={{color: '#0f172a', ...generateTextStyles(pStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}
                  >
                    {blockContent.title}
                  </h2>
                )}
                {blockContent.subtitle && (
                  <p
                    className="text-lg"
                    style={{color: '#475569', ...generateTextStyles(pStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}
                  >
                    {blockContent.subtitle}
                  </p>
                )}
              </div>
            )}
            {pLogos.length === 1 && (
              <div
                className="flex items-center py-4 px-8"
                style={{
                  minHeight: `${pHeight}px`,
                  ...(blockContent.evenSpacing
                    ? { justifyContent: 'space-evenly' }
                    : { justifyContent: 'center', gap: `${pGap}px` }),
                }}
                data-testid="partners-slider"
              >
                {pLogos.map((logo) => {
                  const logoLink = logo.link && !logo.link.startsWith('http') ? `https://${logo.link}` : logo.link;
                  const itemHeight = logo.maxHeight || pHeight;
                  const img = (
                    <CmsImage owner={logo} field="url"
                      src={normLogo(logo.url)}
                      alt={logo.name || 'Partner'}
                      title={logo.name || ''}
                      className="w-auto object-contain"
                      style={{ height: `${itemHeight}px`, width: 'auto', ...(logo.maxWidth ? { maxWidth: `${logo.maxWidth}px` } : {}) }}
                    />
                  );
                  return (
                    <div
                      key={logo.id}
                      className={`${pGray ? 'grayscale-logo' : ''} transition-transform hover:scale-105`}
                    >
                      {logoLink ? (
                        <a
                          href={logoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block h-full"
                          onClick={() => trackClick({ kind: 'partner', label: logo.name || 'Partner', url: logoLink })}
                        >{img}</a>
                      ) : img}
                    </div>
                  );
                })}
              </div>
            )}
            {pLogos.length >= 2 && (() => {
              const minItems = Math.max(10, Math.ceil(1920 / (200 + pGap)) * 2);
              const repeatCount = Math.ceil(minItems / pLogos.length);
              const repeatedLogos = Array.from({ length: repeatCount * 2 }, () => pLogos).flat();
              return (
              <div
                className={`partner-slider-track ${pPause ? 'pause-on-hover' : ''}`}
                style={{
                  '--slider-speed': `${pSpeed}s`,
                  '--slider-gap': `${pGap}px`,
                  '--slider-height': `${pHeight}px`,
                  '--slider-direction': pDirection === 'right' ? 'reverse' : 'normal',
                }}
                data-testid="partners-slider"
              >
                <div className="partner-slider-inner" style={{ height: `${pHeight}px` }}>
                  {repeatedLogos.map((logo, i) => {
                    const logoLink = logo.link && !logo.link.startsWith('http') ? `https://${logo.link}` : logo.link;
                    const itemHeight = logo.maxHeight || pHeight;
                    const img = (
                      <CmsImage owner={logo} field="url"
                        src={normLogo(logo.url)}
                        alt={logo.name || 'Partner'}
                        title={logo.name || ''}
                        className="w-auto object-contain"
                        style={{ height: `${itemHeight}px`, width: 'auto', ...(logo.maxWidth ? { maxWidth: `${logo.maxWidth}px` } : {}) }}
                        loading="lazy"
                      />
                    );
                    return (
                    <div
                      key={`${logo.id || i}-${i}`}
                      className={`partner-logo-item ${pGray ? 'grayscale-logo' : ''}`}
                      style={{ height: '100%', padding: `0 ${pGap / 2}px` }}
                    >
                      {logoLink ? (
                        <a
                          href={logoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block h-full"
                          onClick={() => trackClick({ kind: 'partner', label: logo.name || 'Partner', url: logoLink })}
                        >{img}</a>
                      ) : img}
                    </div>
                    );
                  })}
                </div>
              </div>
              );
            })()}
            {pLogos.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-sm">
                Žádná loga nebyla přidána. Přidejte loga v editoru.
              </div>
            )}
          </section>
        );

      case 'image_text': {
        const itStyles = blockContent.textStyles || {};
        const imgPos = blockContent.imagePosition || 'left';
        const isStacked = imgPos === 'top' || imgPos === 'bottom';
        const layout = blockContent.imageLayout || (blockContent.overlapping ? 'overlap' : 'single');
        const shadowMap = { none: '', sm: 'shadow-sm', md: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-2xl' };
        // Column ratio for side-by-side (left/right)
        const ratioMap = {
          '33-67': 'md:grid-cols-[1fr_2fr]',
          '40-60': 'md:grid-cols-[2fr_3fr]',
          '50-50': 'md:grid-cols-2',
          '60-40': 'md:grid-cols-[3fr_2fr]',
          '67-33': 'md:grid-cols-[2fr_1fr]',
        };
        const colsCls = isStacked ? 'grid-cols-1' : (ratioMap[blockContent.imageColRatio || '50-50'] || 'md:grid-cols-2');
        // Stacked width when image is on top/bottom
        const stackedWidthMap = {
          narrow: 'max-w-[600px]',
          medium: 'max-w-[900px]',
          wide: 'max-w-[1200px]',
          full: 'max-w-none w-full',
        };
        const stackedWidthCls = isStacked ? (stackedWidthMap[blockContent.imageStackedWidth || 'full'] || 'max-w-none w-full') : '';
        // Max image height (applies to single image)
        const maxH = blockContent.imageMaxHeight;
        const maxHeightStyle = (maxH && maxH !== 'auto' && maxH !== 'none')
          ? { maxHeight: `${maxH}px`, objectFit: blockContent.imageFit === 'contain' ? 'contain' : 'cover' }
          : {};

        const img1Style = {
          width: `${blockContent.image1Width ?? 100}%`,
          opacity: (blockContent.imageOpacity ?? 100) / 100,
          transform: blockContent.image1Rotate ? `rotate(${blockContent.image1Rotate}deg)` : undefined,
          borderRadius: `${blockContent.image1Radius ?? 12}px`,
        };
        const img2Style = {
          width: `${blockContent.image2Width ?? 60}%`,
          opacity: (blockContent.image2Opacity ?? 100) / 100,
          transform: blockContent.image2Rotate ? `rotate(${blockContent.image2Rotate}deg)` : undefined,
          borderRadius: `${blockContent.image2Radius ?? 12}px`,
        };
        const img1Shadow = shadowMap[blockContent.image1Shadow ?? 'md'] ?? '';
        const img2Shadow = shadowMap[blockContent.image2Shadow ?? 'md'] ?? '';
        const fitCls = fitClass(blockContent);
        
        const renderImages = () => {
          if (layout === 'side-by-side' && blockContent.image2) {
            return (
              <div className="flex gap-4 items-start">
                <div className="flex-1">
                  {blockContent.image ? <CmsImage owner={blockContent} src={blockContent.image} alt="" className={`${fitCls} ${img1Shadow}`} style={img1Style} /> : <div className="w-full aspect-[4/3] bg-slate-200 rounded-2xl" />}
                </div>
                <div className="flex-1 mt-8">
                  <CmsImage owner={blockContent} field="image2" src={blockContent.image2} alt="" className={`${fitCls} ${img2Shadow}`} style={img2Style} />
                </div>
              </div>
            );
          }
          if (layout === 'stacked' && blockContent.image2) {
            return (
              <div className="space-y-4">
                {blockContent.image ? <CmsImage owner={blockContent} src={blockContent.image} alt="" className={`${fitCls} ${img1Shadow} mx-auto`} style={img1Style} /> : null}
                <CmsImage owner={blockContent} field="image2" src={blockContent.image2} alt="" className={`${fitCls} ${img2Shadow} mx-auto`} style={img2Style} />
              </div>
            );
          }
          if (layout === 'overlap' || blockContent.overlapping) {
            const offsetX = blockContent.image2OffsetX ?? 30;
            const offsetY = blockContent.image2OffsetY ?? 20;
            return (
              <div className="relative" style={{ minHeight: '400px' }}>
                {blockContent.image ? (
                  <CmsImage owner={blockContent} src={blockContent.image} alt="" className={`${fitCls} relative z-10 ${img1Shadow}`} style={img1Style} />
                ) : (
                  <div className="w-[75%] aspect-[4/3] bg-slate-200 rounded-2xl flex items-center justify-center text-slate-400 relative z-10">Hlavní obrázek</div>
                )}
                {blockContent.image2 ? (
                  <CmsImage owner={blockContent} field="image2" src={blockContent.image2} alt="" className={`absolute ${fitCls} z-20 border-4 border-white ${img2Shadow}`} style={{ ...img2Style, right: `${-offsetX}%`, bottom: `${-offsetY}%` }} />
                ) : (
                  <div className="absolute bottom-0 right-0 w-[55%] aspect-[3/4] bg-slate-300 rounded-2xl z-20 border-4 border-white shadow-2xl flex items-center justify-center text-slate-400 text-sm">2. obrázek</div>
                )}
              </div>
            );
          }
          // single image
          if (!blockContent.image) {
            return <div className="w-full aspect-[4/3] bg-slate-200 rounded-2xl flex items-center justify-center text-slate-400">Obrázek</div>;
          }
          const cropCls = cropClass(blockContent);
          const combinedStyle = { ...img1Style, ...cropPos(blockContent), ...maxHeightStyle, ...fitBgStyle(blockContent) };
          // If a fixed aspect ratio is set we want the wrapper to enforce it and the img to fill+fit inside.
          if (cropCls) {
            return (
              <div className={`${cropCls} overflow-hidden rounded-2xl ${img1Shadow} ${isStacked ? stackedWidthCls + ' mx-auto' : 'w-full'}`} style={{ ...fitBgStyle(blockContent), borderRadius: `${blockContent.image1Radius ?? 12}px` }}>
                <CmsImage owner={blockContent} src={blockContent.image} alt={blockContent.title || ''} className={`w-full h-full ${fitCls}`} style={{ objectPosition: blockContent.imageFocalPoint || 'center', opacity: (blockContent.imageOpacity ?? 100) / 100, transform: blockContent.image1Rotate ? `rotate(${blockContent.image1Rotate}deg)` : undefined }} />
              </div>
            );
          }
          // No aspect ratio: render natural with optional max height
          return (
            <CmsImage owner={blockContent}
              src={blockContent.image}
              alt={blockContent.title || ''}
              className={`${fitCls} ${img1Shadow} ${isStacked ? stackedWidthCls + ' mx-auto block' : ''}`}
              style={combinedStyle}
            />
          );
        };
        
        // Determine order classes for image and text wrappers
        let imageOrderCls = '';
        let textOrderCls = '';
        if (imgPos === 'right') { imageOrderCls = 'md:order-2'; textOrderCls = 'md:order-1'; }
        else if (imgPos === 'top') { imageOrderCls = 'order-1'; textOrderCls = 'order-2'; }
        else if (imgPos === 'bottom') { imageOrderCls = 'order-2'; textOrderCls = 'order-1'; }

        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            <ScrollAnimatedSection block={block} className={cw(blockContent)}>
              <div className={`grid ${colsCls} gap-12 ${isStacked ? '' : 'items-center'}`}>
                <div className={`${imageOrderCls} relative`}>
                  {renderImages()}
                </div>
                <div className={`${textOrderCls} ${isStacked ? 'max-w-3xl mx-auto w-full' : ''}`}>
                  {blockContent.subtitle && <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{color: 'var(--theme-primary)', ...generateTextStyles(itStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}>{blockContent.subtitle}</p>}
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" style={{...generateTextStyles(itStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{blockContent.title}</h2>
                  <div className="text-lg leading-relaxed mb-8 whitespace-pre-line rich-content" style={{color: '#64748b', ...generateTextStyles(itStyles, 'description', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}} {...richTextProps(blockContent.description, itStyles, 'description')} />
                  {shouldRenderButton(blockContent) && (() => {
                    const btnType = blockContent.buttonLinkType || 'anchor';
                    const btnHref = buildButtonHref(blockContent);
                    const btnTarget = blockContent.buttonNewTab || btnType === 'external' ? '_blank' : undefined;
                    const btnRel = btnTarget === '_blank' ? 'noopener noreferrer' : undefined;
                    const alignCls = buttonAlignClass(blockContent);
                    return (
                      <div className={`flex ${alignCls}`}>
                        <a href={btnHref} target={btnTarget} rel={btnRel}>
                          <Button size="lg" className="text-white px-6" style={{ backgroundColor: 'var(--theme-primary)' }}>{blockContent.buttonText}<ArrowRight className="ml-2 h-4 w-4" /></Button>
                        </a>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </ScrollAnimatedSection>
          </section>
        );
      }

      case 'video': {
        const getEmbedUrl = (url) => {
          if (!url) return null;
          const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
          if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=${blockContent.autoplay !== false ? 1 : 0}&mute=1&loop=${blockContent.loop !== false ? 1 : 0}&playlist=${ytMatch[1]}&rel=0`;
          const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
          if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=${blockContent.autoplay !== false ? 1 : 0}&muted=1&loop=${blockContent.loop !== false ? 1 : 0}`;
          return null;
        };
        const isMp4 = blockContent.videoUrl?.match(/\.mp4/i);
        const embedUrl = !isMp4 ? getEmbedUrl(blockContent.videoUrl) : null;
        const vidLayout = blockContent.layout || 'full';

        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            <div className={cw(blockContent, vidLayout === 'full' ? 'w-full' : 'container mx-auto')}>
              {(blockContent.title || blockContent.subtitle) && (
                <div className="text-center mb-10">
                  {blockContent.title && <h2 className="text-3xl md:text-4xl font-bold mb-3" style={blockColors.title ? {color: blockColors.title} : {}}>{blockContent.title}</h2>}
                  {blockContent.subtitle && <p className="text-lg text-slate-600" style={blockColors.subtitle ? {color: blockColors.subtitle} : {}}>{blockContent.subtitle}</p>}
                </div>
              )}
              <div className={vidLayout === 'with-text' ? 'grid md:grid-cols-2 gap-10 items-center' : ''}>
                <div className={`relative overflow-hidden rounded-2xl shadow-2xl ${vidLayout === 'full' ? 'max-w-5xl mx-auto' : ''}`} style={{ aspectRatio: '16/9' }}>
                  {isMp4 ? (
                    <video autoPlay={blockContent.autoplay !== false} muted loop={blockContent.loop !== false} playsInline controls className="w-full h-full object-cover">
                      <source src={blockContent.videoUrl} type="video/mp4" />
                    </video>
                  ) : embedUrl ? (
                    <iframe src={embedUrl} className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="Video" />
                  ) : (
                    <div className="w-full h-full bg-slate-200 flex items-center justify-center text-slate-400">Vložte URL videa v editoru</div>
                  )}
                </div>
                {vidLayout === 'with-text' && blockContent.description && (
                  <div>
                    <div className="text-lg leading-relaxed whitespace-pre-line rich-content" style={blockColors.content ? {color: blockColors.content} : {color: '#64748b'}} {...richTextProps(blockContent.description)} />
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      }

      case 'map': {
        const rawAddress = (blockContent.address || '').trim();
        const mapHeight = blockContent.height || '450px';
        const mapStyle = blockContent.style || 'standard';
        const mapZoom = blockContent.zoom || 15;
        const mapFilter = mapStyle === 'dark' 
          ? 'invert(0.92) hue-rotate(180deg) saturate(0.6) brightness(0.9)' 
          : mapStyle === 'grayscale' 
            ? 'grayscale(100%)' 
            : 'none';
        
        // Build iframe src from various input formats
        let mapSrc = '';
        if (rawAddress) {
          if (rawAddress.includes('google.com/maps/embed')) {
            // Already an embed URL — use as is
            mapSrc = rawAddress;
          } else if (rawAddress.startsWith('<iframe')) {
            // User pasted full iframe HTML — extract src
            const m = rawAddress.match(/src=["']([^"']+)["']/);
            if (m) mapSrc = m[1];
          } else if (rawAddress.startsWith('http')) {
            // Generic Google Maps URL (share link, maps.app.goo.gl, etc.) — pass to Google via q= for embed
            mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(rawAddress)}&output=embed`;
          } else {
            // Plain address or GPS — standard embed
            mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(rawAddress)}&z=${mapZoom}&output=embed`;
          }
        }
        
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-0" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            {(blockContent.title || blockContent.subtitle) && (
              <div className={`${cw(blockContent)} text-center py-12 px-4`}>
                {blockContent.title && <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">{blockContent.title}</h2>}
                {blockContent.subtitle && <p className="text-lg text-slate-600">{blockContent.subtitle}</p>}
              </div>
            )}
            <div className={blockContent.fullWidth ? 'w-full' : 'container mx-auto px-4'} style={{ height: mapHeight }}>
              {mapSrc ? (
                <iframe
                  src={mapSrc}
                  className="w-full h-full border-0"
                  style={{ filter: mapFilter }}
                  loading="lazy"
                  title="Mapa"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-200 text-slate-500">
                  <div className="text-center px-4">
                    <p className="font-medium mb-1">Žádná adresa / odkaz</p>
                    <p className="text-sm">Zadej adresu, GPS, Google Maps odkaz nebo embed URL v nastavení bloku</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'parallax': {
        const plHeight = blockContent.height || '500px';
        const plOverlay = blockContent.overlayColor || '#000000';
        const plOpacity = (blockContent.overlayOpacity ?? 30) / 100;
        return (
          <section key={block.id} id={block.anchorId || block.id} className="relative w-full overflow-hidden" style={{ height: plHeight, backgroundColor: blockContent.fallbackColor || '#1a1a1a' }}>
            {blockContent.backgroundImage && (
              <div className="absolute inset-0" style={{
                backgroundImage: `url("${blockContent.backgroundImage}")`,
                ...backgroundDisplayStyle(blockContent, 'backgroundImage', deviceType),
                backgroundAttachment: 'fixed',
                backgroundRepeat: 'no-repeat'
              }} />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: plOverlay, opacity: plOpacity }} />
            {(blockContent.title || blockContent.description) && (
              <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
                {blockContent.title && <h2 className="text-4xl md:text-6xl font-bold mb-4" style={{ color: blockContent.textColor || '#ffffff' }}>{blockContent.title}</h2>}
                {blockContent.description && <div className="text-lg md:text-xl max-w-2xl whitespace-pre-line rich-content" style={{ color: blockContent.textColor || '#ffffffcc' }} {...richTextProps(blockContent.description)} />}
              </div>
            )}
          </section>
        );
      }

      case 'banner': {
        const bnStyles = blockContent.textStyles || {};
        const bnOverlay = blockContent.overlayColor || '#000000';
        const bnOpacity = (blockContent.overlayOpacity ?? 50) / 100;
        const bnAlign = blockContent.textAlign || 'center';
        return (
          <section key={block.id} id={block.anchorId || block.id} className="relative py-24 md:py-32 px-4 overflow-hidden" style={{ backgroundImage: blockContent.backgroundImage ? `url(${blockContent.backgroundImage})` : undefined, ...backgroundDisplayStyle(blockContent, 'backgroundImage', deviceType) }}>
            <div className="absolute inset-0" style={{ backgroundColor: bnOverlay, opacity: bnOpacity }} />
            <ScrollAnimatedSection block={block} className={`${cw(blockContent)} relative z-10 text-${bnAlign}`} style={{ textAlign: bnAlign }}>
              <h2 className="text-3xl md:text-5xl font-bold mb-4" style={{color: '#ffffff', ...generateTextStyles(bnStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{blockContent.title}</h2>
              {blockContent.description && <div className="text-lg md:text-xl max-w-2xl mb-8 whitespace-pre-line rich-content" style={{ color: 'rgba(255,255,255,0.8)', marginLeft: bnAlign === 'center' ? 'auto' : undefined, marginRight: bnAlign === 'center' ? 'auto' : undefined, ...generateTextStyles(bnStyles, 'description', deviceType), ...(blockColors.content ? {color: blockColors.content} : {}) }} {...richTextProps(blockContent.description, bnStyles, 'description')} />}
              {shouldRenderButton(blockContent) && (() => {
                const btnType = blockContent.buttonLinkType || 'anchor';
                const btnHref = buildButtonHref(blockContent);
                const btnTarget = blockContent.buttonNewTab || btnType === 'external' ? '_blank' : undefined;
                const btnRel = btnTarget === '_blank' ? 'noopener noreferrer' : undefined;
                const alignCls = buttonAlignClass(blockContent);
                return (
                  <div className={`flex ${alignCls}`}>
                    <a href={btnHref} target={btnTarget} rel={btnRel}>
                      <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 px-8 font-semibold">{blockContent.buttonText}<ArrowRight className="ml-2 h-4 w-4" /></Button>
                    </a>
                  </div>
                );
              })()}
            </ScrollAnimatedSection>
          </section>
        );
      }

      case 'gallery': {
        const galStyles = blockContent.textStyles || {};
        const galCols = blockContent.columns || 3;
        const galLayout = blockContent.galleryLayout || 'grid';
        const galGap = blockContent.gap ?? 16;
        const galRadius = blockContent.itemRadius ?? 12;
        const aspectMap = { square: 'aspect-square', landscape: 'aspect-[4/3]', widescreen: 'aspect-[16/9]', portrait: 'aspect-[3/4]', tall: 'aspect-[2/3]', auto: '' };
        const aspectCls = aspectMap[blockContent.aspectRatio || 'square'] || 'aspect-square';
        const hoverEffect = blockContent.hoverEffect || 'zoom';
        const captionStyle = blockContent.captionStyle || 'overlay';
        const colsMap = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4', 5: 'md:grid-cols-5' };
        
        const getHoverCls = () => {
          switch (hoverEffect) {
            case 'zoom': return 'group-hover:scale-110';
            case 'fade': return 'group-hover:opacity-70';
            case 'lift': return 'group-hover:-translate-y-2';
            case 'rotate': return 'group-hover:rotate-2';
            default: return '';
          }
        };
        
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            <div className={cw(blockContent)}>
              {(blockContent.title || blockContent.subtitle) && (
                <div className="text-center mb-12">
                  {blockContent.title && <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{...generateTextStyles(galStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{blockContent.title}</h2>}
                  {blockContent.subtitle && <p className="text-lg" style={{color: '#475569', ...generateTextStyles(galStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}>{blockContent.subtitle}</p>}
                </div>
              )}
              {galLayout === 'masonry' ? (
                <div className={`columns-2 ${colsMap[galCols]?.replace('grid-cols-', 'columns-md-') || 'md:columns-3'}`} style={{ columnGap: `${galGap}px` }}>
                  {(blockContent.items || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((item, i) => (
                    <a key={item.id || i} href={item.link || undefined} className={`group block break-inside-avoid mb-4 relative overflow-hidden ${item.link ? 'cursor-pointer' : ''}`} style={{ borderRadius: `${galRadius}px`, marginBottom: `${galGap}px` }}>
                      {item.image ? <CmsImage owner={item} src={item.image} alt={item.title || ''} className={`w-full h-auto ${itemFitClass(item, 'image', blockContent)} transition-all duration-500 ${getHoverCls()}`} style={itemCropPos(item, 'image', blockContent)} /> : <div className="w-full h-48 bg-slate-200" />}
                      {item.title && captionStyle !== 'none' && (
                        captionStyle === 'below' ? <p className="text-sm mt-2" style={{color: '#334155', ...generateTextStyles(galStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{item.title}</p> :
                        captionStyle === 'always' ? <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4"><p className="font-medium" style={{color: '#ffffff', ...generateTextStyles(galStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{item.title}</p></div> :
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4"><p className="font-medium" style={{color: '#ffffff', ...generateTextStyles(galStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{item.title}</p></div>
                      )}
                    </a>
                  ))}
                </div>
              ) : (
                <div className={`grid grid-cols-2 ${colsMap[galCols] || 'md:grid-cols-3'}`} style={{ gap: `${galGap}px` }}>
                  {(blockContent.items || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((item, i) => {
                    const staggered = galLayout === 'staggered' && i % 2 === 1 ? 'md:mt-8' : '';
                    return (
                      <ScrollAnimatedCard key={item.id || i} block={block} index={i}>
                        <a href={item.link || undefined} className={`group block relative overflow-hidden transition-all duration-500 ${staggered} ${item.link ? 'cursor-pointer' : ''} ${aspectCls}`} style={{ borderRadius: `${galRadius}px` }}>
                          {item.image ? <CmsImage owner={item} src={item.image} alt={item.title || ''} className={`w-full h-full ${itemFitClass(item, 'image', blockContent)} transition-all duration-500 ${getHoverCls()}`} style={itemCropPos(item, 'image', blockContent)} /> : <div className="w-full h-full bg-slate-200 flex items-center justify-center text-slate-400">Obrázek</div>}
                          {item.title && captionStyle !== 'none' && (
                            captionStyle === 'below' ? null :
                            captionStyle === 'always' ? <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4"><p className="font-medium" style={{color: '#ffffff', ...generateTextStyles(galStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{item.title}</p></div> :
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4"><p className="font-medium" style={{color: '#ffffff', ...generateTextStyles(galStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{item.title}</p></div>
                          )}
                        </a>
                        {item.title && captionStyle === 'below' && <p className="text-sm mt-2" style={{color: '#334155', ...generateTextStyles(galStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{item.title}</p>}
                      </ScrollAnimatedCard>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'pricing': {
        const prStyles = blockContent.textStyles || {};
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : { backgroundColor: '#f8fafc' }}>
            <div className={cw(blockContent)}>
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{...generateTextStyles(prStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{blockContent.title || 'Ceník'}</h2>
                {blockContent.subtitle && <p className="text-lg" style={{color: '#475569', ...generateTextStyles(prStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}>{blockContent.subtitle}</p>}
              </div>
              <div className={`grid grid-cols-1 md:grid-cols-${Math.min((blockContent.items || []).length, 3)} gap-8 max-w-5xl mx-auto`}>
                {(blockContent.items || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((item, i) => (
                  <ScrollAnimatedCard key={item.id || i} block={block} index={i}>
                    <Card className={`h-full transition-all duration-300 hover:-translate-y-2 ${item.highlighted ? 'border-2 shadow-xl scale-105' : 'border-slate-200'}`} style={item.highlighted ? { borderColor: 'var(--theme-primary)' } : {}}>
                      {item.highlighted && <div className="text-white text-center text-sm font-semibold py-1.5 rounded-t-lg" style={{ backgroundColor: 'var(--theme-primary)' }}>Doporučeno</div>}
                      <CardHeader className="text-center pb-2">
                        <CardTitle className="text-xl" style={{...generateTextStyles(prStyles, 'subtitle', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{item.title}</CardTitle>
                        <div className="mt-4">
                          <span className="text-4xl font-bold" style={blockColors.title ? {color: blockColors.title} : {}}>{item.price}</span>
                          <span className="ml-1" style={{color: '#64748b', ...generateTextStyles(prStyles, 'description', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}}>{item.period}</span>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {item.description && <div className="text-sm mb-6 text-center whitespace-pre-line rich-content" style={{color: '#475569', ...generateTextStyles(prStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}} {...richTextProps(item.description, prStyles, 'subtitle')} />}
                        <Button className={`w-full ${item.highlighted ? 'text-white' : ''}`} style={item.highlighted ? { backgroundColor: 'var(--theme-primary)' } : {}} variant={item.highlighted ? 'default' : 'outline'}>{item.buttonText || 'Vybrat'}</Button>
                      </CardContent>
                    </Card>
                  </ScrollAnimatedCard>
                ))}
              </div>
            </div>
          </section>
        );
      }

      case 'team': {
        const tmStyles = blockContent.textStyles || {};
        const tmCols = blockContent.columns || 3;
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            <div className={cw(blockContent)}>
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{...generateTextStyles(tmStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{blockContent.title || 'Náš tým'}</h2>
                {blockContent.subtitle && <p className="text-lg" style={{color: '#475569', ...generateTextStyles(tmStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}>{blockContent.subtitle}</p>}
              </div>
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${tmCols} gap-8`}>
                {(blockContent.items || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((item, i) => (
                  <ScrollAnimatedCard key={item.id || i} block={block} index={i}>
                    <div className="group text-center">
                      <div className="w-40 h-40 mx-auto rounded-full overflow-hidden mb-5 ring-4 ring-slate-100 group-hover:ring-blue-100 transition-all">
                        {item.image ? (
                          <CmsImage owner={item} src={item.image} alt={item.title || ''} className={`w-full h-full ${itemFitClass(item, 'image', blockContent)}`} style={itemCropPos(item, 'image', blockContent)} />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-4xl font-bold text-slate-400">{(item.title || '?')[0]}</div>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-1" style={{...generateTextStyles(tmStyles, 'subtitle', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{item.title}</h3>
                      {item.subtitle && <p className="text-sm font-medium mb-3" style={{color: '#2563eb', ...generateTextStyles(tmStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}>{item.subtitle}</p>}
                      {item.description && <div className="text-sm max-w-xs mx-auto rich-content mb-3" style={{color: '#64748b', ...generateTextStyles(tmStyles, 'subtitle', deviceType), ...(blockColors.content ? {color: blockColors.content} : {})}} {...richTextProps(item.description, tmStyles, 'subtitle')} />}
                      {(item.email || item.phone) && (
                        <div className="flex flex-col items-center gap-1 text-sm text-slate-600 mb-2">
                          {item.email && (
                            <a href={`mailto:${item.email}`} className="hover:text-blue-600 transition-colors inline-flex items-center gap-1.5">
                              {React.createElement(getIcon('Mail'), { className: 'h-3.5 w-3.5' })}
                              <span>{item.email}</span>
                            </a>
                          )}
                          {item.phone && (
                            <a href={`tel:${String(item.phone).replace(/\s+/g, '')}`} className="hover:text-blue-600 transition-colors inline-flex items-center gap-1.5">
                              {React.createElement(getIcon('Phone'), { className: 'h-3.5 w-3.5' })}
                              <span>{item.phone}</span>
                            </a>
                          )}
                        </div>
                      )}
                      {Array.isArray(item.extraLines) && item.extraLines.length > 0 && (
                        <ul className="flex flex-col items-center gap-1 text-sm text-slate-600 mt-2">
                          {item.extraLines.map((line) => {
                            const LIcon = getIcon(line.icon || 'Info');
                            const v = line.value || '';
                            const isEmail = v.includes('@');
                            const isPhone = !isEmail && /^[+\d\s()-]+$/.test(v);
                            const isUrl = /^https?:\/\//i.test(v);
                            const inner = (
                              <span className="inline-flex items-center gap-1.5">
                                <LIcon className="h-3.5 w-3.5" />
                                {line.label && <strong className="font-medium">{line.label}:</strong>}
                                <span>{v}</span>
                              </span>
                            );
                            const href = isEmail ? `mailto:${v}` : isPhone ? `tel:${v.replace(/\s+/g, '')}` : isUrl ? v : null;
                            return (
                              <li key={line.id}>
                                {href ? <a href={href} className="hover:text-blue-600 transition-colors" target={isUrl ? '_blank' : undefined} rel={isUrl ? 'noopener noreferrer' : undefined}>{inner}</a> : inner}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </ScrollAnimatedCard>
                ))}
              </div>
            </div>
          </section>
        );
      }

      case 'breadcrumbs': {
        const bcItems = blockContent.items || [];
        return (
          <nav key={block.id} aria-label="Drobečková navigace" className="px-4 pt-6" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            <div className="container mx-auto max-w-5xl">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                {bcItems.map((it, i) => (
                  <li key={i} className="flex items-center gap-2">
                    {i > 0 && <span className="text-slate-300">/</span>}
                    {it.href ? <a href={it.href} className="hover:text-slate-700 transition-colors">{it.label}</a> : <span className="text-slate-700">{it.label}</span>}
                  </li>
                ))}
              </ol>
            </div>
          </nav>
        );
      }

      case 'steps': {
        const stepItems = blockContent.items || [];
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            <div className="container mx-auto max-w-6xl">
              {blockContent.title && (
                <div className="text-center mb-14">
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={blockColors.title ? { color: blockColors.title } : {}}>{blockContent.title}</h2>
                  {blockContent.subtitle && <p className="text-lg text-slate-600 max-w-2xl mx-auto">{blockContent.subtitle}</p>}
                </div>
              )}
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {stepItems.map((it, i) => {
                  const StepIcon = typeof it.icon === 'string' ? (getIcon(it.icon) || LucideIcons.Circle) : LucideIcons.Circle;
                  return (
                    <ScrollAnimatedCard key={it.id || i} block={block} index={i}>
                      <div className="bg-white border rounded-2xl p-6 h-full" style={{ borderColor: '#DEE4ED' }}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: 'var(--theme-primary)' }}>{i + 1}</div>
                          <StepIcon className="h-5 w-5" style={{ color: 'var(--theme-primary)' }} />
                        </div>
                        <div className="font-semibold text-slate-900 mb-1">{it.title}</div>
                        {it.description && <div className="text-sm text-slate-600 whitespace-pre-line">{it.description}</div>}
                      </div>
                    </ScrollAnimatedCard>
                  );
                })}
              </div>
            </div>
          </section>
        );
      }

      case 'table': {
        const tCols = blockContent.columns || [];
        const tRows = blockContent.rows || [];
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            <div className="container mx-auto max-w-5xl">
              {blockContent.title && (
                <div className="text-center mb-10">
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={blockColors.title ? { color: blockColors.title } : {}}>{blockContent.title}</h2>
                  {blockContent.subtitle && <p className="text-lg text-slate-600 max-w-2xl mx-auto">{blockContent.subtitle}</p>}
                </div>
              )}
              <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: '#DEE4ED' }}>
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50">
                      {tCols.map((c, i) => <th key={i} className="p-4 text-sm font-semibold text-slate-700 border-b" style={{ borderColor: '#DEE4ED' }}>{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {tRows.map((r, ri) => (
                      <tr key={ri} className="hover:bg-slate-50">
                        {(r || []).map((cell, ci) => <td key={ci} className="p-4 text-sm text-slate-600 border-b align-top" style={{ borderColor: '#EEF1F6' }}>{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {blockContent.note && <p className="text-xs text-slate-400 mt-3">{blockContent.note}</p>}
            </div>
          </section>
        );
      }

      case 'faq': {
        const faqStyles = blockContent.textStyles || {};
        return (
          <section key={block.id} id={block.anchorId || block.id} className="py-20 px-4" style={blockColors.bg ? { backgroundColor: blockColors.bg } : {}}>
            <div className={cw(blockContent, 'container mx-auto max-w-3xl')}>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{...generateTextStyles(faqStyles, 'title', deviceType), ...(blockColors.title ? {color: blockColors.title} : {})}}>{blockContent.title || 'Časté dotazy'}</h2>
                {blockContent.subtitle && <p className="text-lg" style={{color: '#475569', ...generateTextStyles(faqStyles, 'subtitle', deviceType), ...(blockColors.subtitle ? {color: blockColors.subtitle} : {})}}>{blockContent.subtitle}</p>}
              </div>
              <div className="space-y-4">
                {(blockContent.items || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((item, i) => (
                  <FaqItem key={item.id || i} item={item} blockColors={blockColors} faqStyles={faqStyles} deviceType={deviceType} />
                ))}
              </div>
            </div>
          </section>
        );
      }

      case 'footer': {
        const fContent = blockContent;
        const fLayout = fContent.layout || 'columns';
        const fCols = fContent.columns || 3;
        const fLogoPos = fContent.logoPosition || 'left';
        const fBg = blockColors.bg || fContent.backgroundColor || '#0f172a';
        const fBgOpacity = (fContent.backgroundOpacity ?? 100) / 100;
        // Helper: compute rgba from hex + opacity (so parallax shows through)
        const hexToRgba = (hex, alpha) => {
          if (!hex || !hex.startsWith('#')) return hex;
          const h = hex.replace('#', '');
          const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
          const r = parseInt(full.substr(0, 2), 16);
          const g = parseInt(full.substr(2, 2), 16);
          const b = parseInt(full.substr(4, 2), 16);
          return `rgba(${r},${g},${b},${alpha})`;
        };
        const fBgFinal = fBgOpacity < 1 ? hexToRgba(fBg, fBgOpacity) : fBg;
        const fTextColor = fContent.textColor || '#ffffff';
        const fLogoH = fContent.logoHeight || 40;
        const fShowDesc = fContent.showDescription !== false;
        const fNavLinks = (fContent.navLinks || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const fContactItems = (fContent.contactItems || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const fSocialLinks = fContent.socialLinks || [];
        const fLegalLinks = fContent.legalLinks || [];
        const fColumnsData = fContent.columnsData || [];
        const copyrightText = (fContent.copyright || '© {year} TechDesk.').replace('{year}', new Date().getFullYear());
        
        const LogoBlock = (
          <div className={`flex items-center gap-3 mb-4 ${fLogoPos === 'center' ? 'justify-center' : fLogoPos === 'right' ? 'justify-end' : 'justify-start'}`}>
            {fContent.logoUrl && (
              <CmsImage owner={fContent} field="logoUrl" src={fContent.logoUrl} alt="Logo" className={`w-auto object-contain ${fContent.logoInvert ? 'brightness-0 invert' : ''}`} style={{ height: `${fLogoH}px` }} />
            )}
            {fContent.companyName && <h3 className="text-2xl font-bold">{fContent.companyName}</h3>}
          </div>
        );
        
        const SocialRow = fSocialLinks.length > 0 && (
          <div className={`flex gap-3 mt-4 ${fLogoPos === 'center' ? 'justify-center' : ''}`}>
            {fSocialLinks.map((s, i) => {
              const Icon = getIcon(s.icon);
              return (
                <a key={i} href={s.url || '#'} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full flex items-center justify-center opacity-70 hover:opacity-100 transition-all" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        );
        
        // Layout: minimal (centered legal + copyright)
        if (fLayout === 'minimal') {
          return (
            <footer key={block.id} id={block.anchorId || block.id} className="py-6 px-6" style={{ backgroundColor: fBgFinal, color: fTextColor }}>
              <div className={`${cw(fContent, 'max-w-6xl mx-auto')} flex flex-col items-center`}>
                {LogoBlock}
                {fLegalLinks.length > 0 && (
                  <div className="flex flex-wrap items-center justify-center gap-10 mb-3">
                    {fLegalLinks.map((ll, i) => (
                      <a key={i} href={ll.url || '#'} className="text-sm opacity-80 hover:opacity-100 transition-opacity">{ll.label}</a>
                    ))}
                  </div>
                )}
                <p className="text-sm opacity-70">{copyrightText}</p>
                {SocialRow}
              </div>
            </footer>
          );
        }
        
        // Layout: split (logo left, links right)
        if (fLayout === 'split') {
          return (
            <footer key={block.id} id={block.anchorId || block.id} className="py-12 px-6" style={{ backgroundColor: fBgFinal, color: fTextColor }}>
              <div className={cw(fContent, 'max-w-6xl mx-auto')}>
                <div className="flex flex-col md:flex-row justify-between gap-8 mb-8">
                  <div className="md:max-w-sm">
                    {LogoBlock}
                    {fShowDesc && fContent.description && <div className="opacity-70 text-sm leading-relaxed whitespace-pre-line rich-content" {...richTextProps(fContent.description)} />}
                    {SocialRow}
                  </div>
                  <div className="flex gap-10 flex-wrap">
                    {fNavLinks.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 text-sm uppercase opacity-80">Navigace</h4>
                        <ul className="space-y-2">{fNavLinks.map(l => <li key={l.id}><a href={l.href} className="text-sm opacity-70 hover:opacity-100">{l.label}</a></li>)}</ul>
                      </div>
                    )}
                    {fContactItems.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 text-sm uppercase opacity-80">Kontakt</h4>
                        <ul className="space-y-2">{fContactItems.map(c => {
                          const Icon = getIcon(c.icon);
                          const ct = c.type === 'email' ? 'email' : c.type === 'phone' ? 'phone' : (c.value?.includes('@') ? 'email' : c.value?.match(/^[+\d\s()-]+$/) ? 'phone' : 'text');
                          const href = ct === 'email' ? `mailto:${c.value}` : ct === 'phone' ? `tel:${String(c.value).replace(/\s+/g, '')}` : null;
                          return (
                            <li key={c.id} className="flex items-center gap-2 text-sm opacity-70">
                              <Icon className="h-4 w-4" />
                              {href
                                ? <a href={href} className="hover:opacity-100 transition-opacity" data-testid={`footer-contact-${ct}`}>{c.value}</a>
                                : <span>{c.value}</span>}
                            </li>
                          );
                        })}</ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t pt-4 flex flex-wrap justify-between items-center gap-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                  <p className="text-xs opacity-60">{copyrightText}</p>
                  {fLegalLinks.length > 0 && <div className="flex gap-4">{fLegalLinks.map((ll, i) => <a key={i} href={ll.url || '#'} className="text-xs opacity-60 hover:opacity-100">{ll.label}</a>)}</div>}
                </div>
              </div>
            </footer>
          );
        }
        
        // Layout: centered
        if (fLayout === 'centered') {
          return (
            <footer key={block.id} id={block.anchorId || block.id} className="py-12 px-6 text-center" style={{ backgroundColor: fBgFinal, color: fTextColor }}>
              <div className={cw(fContent, 'max-w-4xl mx-auto')}>
                {LogoBlock}
                {fShowDesc && fContent.description && <div className="opacity-70 text-sm max-w-xl mx-auto mb-6 whitespace-pre-line rich-content" {...richTextProps(fContent.description)} />}
                {fNavLinks.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-6 mb-6">
                    {fNavLinks.map(l => <a key={l.id} href={l.href} className="text-sm opacity-70 hover:opacity-100">{l.label}</a>)}
                  </div>
                )}
                {SocialRow}
                <div className="border-t mt-6 pt-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                  <p className="text-xs opacity-60">{copyrightText}</p>
                  {fLegalLinks.length > 0 && <div className="flex flex-wrap justify-center gap-4 mt-2">{fLegalLinks.map((ll, i) => <a key={i} href={ll.url || '#'} className="text-xs opacity-60 hover:opacity-100">{ll.label}</a>)}</div>}
                </div>
              </div>
            </footer>
          );
        }
        
        // Default: columns layout (supports custom columnsData or fallback to nav+contact)
        const gridCols = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4', 5: 'md:grid-cols-5' }[fCols] || 'md:grid-cols-3';
        return (
          <footer key={block.id} id={block.anchorId || block.id} className="py-12 px-6" style={{ backgroundColor: fBgFinal, color: fTextColor }}>
            <div className={cw(fContent, 'max-w-7xl mx-auto')}>
              <div className={`grid grid-cols-1 ${gridCols} gap-8 items-start`}>
                {/* Logo + description column */}
                <div className={fColumnsData.length === 0 && fCols >= 3 ? 'md:col-span-2' : ''}>
                  {LogoBlock}
                  {fShowDesc && fContent.description && <div className="opacity-70 text-sm leading-relaxed max-w-md whitespace-pre-line rich-content" {...richTextProps(fContent.description)} />}
                  {SocialRow}
                </div>
                
                {/* Custom columns data (user-defined) */}
                {fColumnsData.length > 0 ? fColumnsData.map((col, i) => {
                  const lines = (col.links || '').split('\n').map(l => l.trim()).filter(Boolean);
                  return (
                    <div key={col.id || i}>
                      <h4 className="font-semibold mb-3 text-sm uppercase opacity-80">{col.title}</h4>
                      <ul className="space-y-2">
                        {lines.map((line, j) => {
                          const [label, href] = line.split('|').map(s => s.trim());
                          return <li key={j}><a href={href || '#'} className="text-sm opacity-70 hover:opacity-100">{label}</a></li>;
                        })}
                      </ul>
                    </div>
                  );
                }) : (
                  <>
                    {/* Default: Navigation column */}
                    {fNavLinks.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 text-sm uppercase opacity-80">Navigace</h4>
                        <ul className="space-y-2">
                          {fNavLinks.map((link) => (
                            <li key={link.id}>
                              <a href={link.href} className="text-sm opacity-70 hover:opacity-100 transition-colors" target={link.href?.startsWith('http') ? '_blank' : undefined} rel={link.href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
                                {link.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Default: Contact column */}
                    {fContactItems.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 text-sm uppercase opacity-80">Kontakt</h4>
                        <ul className="space-y-3">
                          {fContactItems.map((contact) => {
                            const Icon = getIcon(contact.icon);
                            // Auto-detect contact type when not explicitly set
                            const ct = contact.type === 'email' ? 'email'
                              : contact.type === 'phone' ? 'phone'
                              : (contact.value?.includes('@') ? 'email'
                                : contact.value?.match(/^[+\d\s()-]+$/) ? 'phone'
                                : 'text');
                            return (
                              <li key={contact.id} className="text-sm opacity-70">
                                {ct === 'email' ? (
                                  <a href={`mailto:${contact.value}`} className="hover:opacity-100 flex items-center gap-2" data-testid="footer-contact-email"><Icon className="h-4 w-4 flex-shrink-0" /><span>{contact.value}</span></a>
                                ) : ct === 'phone' ? (
                                  <a href={`tel:${String(contact.value).replace(/\s+/g, '')}`} className="hover:opacity-100 flex items-center gap-2" data-testid="footer-contact-phone"><Icon className="h-4 w-4 flex-shrink-0" /><span>{contact.value}</span></a>
                                ) : (
                                  <div className="flex items-center gap-2"><Icon className="h-4 w-4 flex-shrink-0" /><span>{contact.value}</span></div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="border-t mt-8 pt-6 flex flex-wrap justify-between items-center gap-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-sm opacity-60">{copyrightText}</p>
                {fLegalLinks.length > 0 && (
                  <div className="flex flex-wrap gap-4">
                    {fLegalLinks.map((ll, i) => <a key={i} href={ll.url || '#'} className="text-xs opacity-60 hover:opacity-100">{ll.label}</a>)}
                  </div>
                )}
              </div>
            </div>
          </footer>
        );
      }

      case 'presentation': {
        const c = blockContent;
        return (
          <section
            id={block.anchor || `block-${block.id}`}
            style={blockColors.bg !== undefined ? { backgroundColor: blockColors.bg } : undefined}
          >
            <PresentationFrame
              presentationId={c.presentationId}
              heightMode={c.heightMode || 'auto'}
              fixedHeight={c.height || 800}
              fullWidth={c.fullWidth !== false}
              showOpenButton={!!c.showOpenButton}
            />
          </section>
        );
      }

      default:
        return null;
    }
  };

  // Navigation source: page-level override beats global config when provided.
  const effectiveNav = pageNavigation || cmsConfig?.navigation || {};
  const navStyle = effectiveNav.style || {};
  const navItems = effectiveNav.items || [];
  const sortedNavItems = navItems.filter(i => i.visible !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  
  const shadowMap = { none: 'none', sm: '0 1px 3px rgba(0,0,0,0.08)', md: '0 4px 12px rgba(0,0,0,0.1)', lg: '0 10px 30px rgba(0,0,0,0.12)' };
  const bgOpacityValue = (navStyle.bgOpacity ?? 95) / 100;
  const headerStyle = {
    backgroundColor: navStyle.bgColor || undefined,
    opacity: bgOpacityValue < 1 ? undefined : undefined,
    backdropFilter: navStyle.blur !== false ? 'blur(12px)' : 'none',
    WebkitBackdropFilter: navStyle.blur !== false ? 'blur(12px)' : 'none',
    borderBottom: navStyle.borderBottom !== false ? `1px solid ${navStyle.bgColor ? navStyle.bgColor + '30' : '#e2e8f0'}` : 'none',
    boxShadow: shadowMap[navStyle.shadow || 'sm'] || shadowMap.sm,
  };

  // We use a pseudo-background approach: the header has position relative,
  // and an absolute div behind content handles bg color + opacity separately
  // so text stays fully visible while background can be transparent

  const getNavHref = (item) => {
    // Custom URL (external or explicit)
    if (item.href && !item.pageSlug) {
      const h = String(item.href).trim();
      // If marked as external OR looks like a domain without protocol, prepend https://
      const isAnchor = h.startsWith('#') || h.startsWith('/') || h.startsWith('mailto:') || h.startsWith('tel:');
      const hasProtocol = /^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//.test(h);
      if (!hasProtocol && !isAnchor && (item.isExternal || /\.[a-z]{2,}(\/.*)?$/i.test(h))) {
        return `https://${h.replace(/^\/+/, '')}`;
      }
      return h;
    }
    // Block anchor (optionally on another page)
    if (item.targetBlockId) {
      const targetSlug = item.targetPageSlug || '/';
      if (targetSlug === '/' || !targetSlug) {
        return `/#${item.targetBlockId}`;
      }
      // Subpage slug may already start with "/" (e.g. "/gdpr")
      const slug = targetSlug.startsWith('/') ? targetSlug : `/${targetSlug}`;
      return `${slug}#${item.targetBlockId}`;
    }
    // Whole page navigation
    if (item.pageSlug) {
      const slug = item.pageSlug.startsWith('/') ? item.pageSlug : `/${item.pageSlug}`;
      return slug;
    }
    return '#';
  };

  if (!cmsConfig) {
    return <div className="tdp-site min-h-screen flex items-center justify-center" data-testid="public-loading"><div role={loadError ? 'alert' : 'status'} data-testid="public-loading-status">{loadError ? <><p>Web se nepodařilo načíst.</p><button type="button" className="tdp-button" data-testid="public-retry" onClick={() => window.location.reload()}>Zkusit znovu</button></> : 'Načítání…'}</div></div>;
  }

  if (activeBlocks.some(block => block.content?.publicDesign === 'editorial-v3')) {
    return <PublicSite config={cmsConfig || {}} blocks={sortedBlocks} navigation={effectiveNav} sharedHeader={sharedHeader} onLogoClick={handleLogoClick} renderFallback={renderBlock} isHome={!overrideBlocks} />;
  }

  return (
    <div className="td-site min-h-screen bg-white relative">
      <link rel="stylesheet" href="/td-site.css" />
      {/* AI proposal preview banner — only shown when ?preview_proposal=<id> */}
      {cmsConfig?._preview && <PreviewBanner info={cmsConfig._preview} />}
      {/* Global parallax background (applies to whole page) */}
      {globalParallaxBlock && (
        <>
          <div className="fixed inset-0 z-0 pointer-events-none" style={{
            backgroundImage: `url("${globalParallaxBlock.content.backgroundImage}")`,
            ...backgroundDisplayStyle(globalParallaxBlock.content, 'backgroundImage', deviceType),
            backgroundAttachment: 'fixed',
            backgroundRepeat: 'no-repeat',
            opacity: (globalParallaxBlock.content.backgroundImageOpacity ?? 100) / 100,
          }} />
          {(globalParallaxBlock.content.overlayOpacity ?? 30) > 0 && (
            <div className="fixed inset-0 z-0 pointer-events-none" style={{
              backgroundColor: globalParallaxBlock.content.overlayColor || '#000000',
              opacity: (globalParallaxBlock.content.overlayOpacity ?? 30) / 100,
            }} />
          )}
        </>
      )}
      <div className="relative z-[1]">
      {/* Header — hide on subpages when sharedHeader=false (page provides its own header in blocks) */}
      {(!overrideBlocks || sharedHeader) && (
      <header
        className={`sticky top-0 z-50 transition-all duration-300 relative ${
          !navStyle.bgColor ? (headerScrolled ? 'header-scrolled' : '') : ''
        }`}
        style={{
          backdropFilter: navStyle.blur !== false ? 'blur(12px)' : 'none',
          WebkitBackdropFilter: navStyle.blur !== false ? 'blur(12px)' : 'none',
          borderBottom: navStyle.borderBottom !== false ? `1px solid ${navStyle.bgColor ? navStyle.bgColor + '30' : '#e2e8f0'}` : 'none',
          boxShadow: shadowMap[navStyle.shadow || 'sm'] || shadowMap.sm,
        }}
        data-testid="main-header"
      >
        {/* Background layer with adjustable opacity */}
        <div className="absolute inset-0 transition-opacity duration-300" style={{
          backgroundColor: navStyle.bgColor || '#ffffff',
          opacity: bgOpacityValue,
        }} />
        <div className={`${navStyle.fullWidth ? 'w-full px-4 md:px-8' : 'container mx-auto px-4'} transition-all duration-300 relative z-10 ${headerScrolled ? 'py-2' : 'py-4'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center cursor-pointer" onClick={handleLogoClick} style={{ marginLeft: navStyle.logoMarginLeft ? `${navStyle.logoMarginLeft}px` : undefined }}>
              {effectiveNav.logo && (
                <CmsImage owner={effectiveNav} field="logo"
                  src={effectiveNav.logo} 
                  alt={effectiveNav.logoAlt || "Logo"} 
                  className={`w-auto object-contain transition-all duration-300 ${headerScrolled ? 'h-10' : 'h-16'}`}
                />
              )}
            </div>

            {/* Desktop Nav — dynamic from CMS */}
            {(() => {
              const itemsAlign = navStyle.itemsAlign || 'right';
              const justifyMap = { left: 'justify-start ml-6', center: 'justify-center flex-1', right: 'justify-end', 'space-between': 'justify-between flex-1 ml-6' };
              const justifyCls = justifyMap[itemsAlign] || 'justify-end';
              const itemGapPx = navStyle.itemGap ?? 32;
              const navFontStyle = {
                fontFamily: navStyle.fontFamily && navStyle.fontFamily !== 'inherit' ? `"${navStyle.fontFamily}", sans-serif` : undefined,
                fontWeight: navStyle.fontWeight || undefined,
                fontSize: navStyle.fontSize ? `${navStyle.fontSize}px` : undefined,
                letterSpacing: navStyle.letterSpacing != null ? `${navStyle.letterSpacing}px` : undefined,
                textTransform: navStyle.textTransform && navStyle.textTransform !== 'none' ? navStyle.textTransform : undefined,
              };
              const hoverColor = navStyle.hoverColor || 'var(--theme-primary)';
              const hoverUnderline = navStyle.hoverUnderline || 'none';
              return (
                <nav className={`hidden md:flex items-center ${justifyCls}`} style={{ gap: `${itemGapPx}px`, marginRight: navStyle.itemsMarginRight ? `${navStyle.itemsMarginRight}px` : undefined }}>
                  {sortedNavItems.map((item) => {
                    if (item.isButton) {
                      return (
                        <a 
                          key={item.id}
                          href={getNavHref(item)}
                          target={item.isExternal ? '_blank' : undefined}
                          rel={item.isExternal ? 'noopener noreferrer' : undefined}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg"
                          style={{
                            backgroundColor: navStyle.buttonBgColor || 'var(--theme-primary)',
                            color: navStyle.buttonTextColor || '#ffffff',
                            ...navFontStyle,
                          }}
                        >
                          {item.label}
                          {item.isExternal && <ExternalLink className="h-4 w-4" />}
                        </a>
                      );
                    }
                    return (
                      <a 
                        key={item.id}
                        href={getNavHref(item)}
                        target={item.isExternal ? '_blank' : undefined}
                        rel={item.isExternal ? 'noopener noreferrer' : undefined}
                        className={`nav-link-dynamic transition-colors ${hoverUnderline === 'underline' ? 'nav-hover-underline' : hoverUnderline === 'bar' ? 'nav-hover-bar' : ''}`}
                        style={{ color: navStyle.textColor || '#334155', ...navFontStyle, ['--nav-hover-color']: hoverColor }}
                      >
                        {item.label}
                      </a>
                    );
                  })}
                </nav>
              );
            })()}

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
              style={{ color: navStyle.textColor || '#334155' }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Nav Overlay — uses dedicated mobileMenuCustom settings if enabled,
              otherwise falls back to the desktop nav style. */}
          {isMobileMenuOpen && (() => {
            const useCustom = !!navStyle.mobileMenuCustom;
            const mobBg = useCustom ? (navStyle.mobileBgColor || navStyle.bgColor || '#ffffff') : (navStyle.bgColor || '#ffffff');
            const mobOpacity = (useCustom ? (navStyle.mobileBgOpacity ?? 95) : (navStyle.bgOpacity ?? 95)) / 100;
            const mobBlur = useCustom ? (navStyle.mobileBlur !== false) : (navStyle.blur !== false);
            return (
              <div
                className="md:hidden absolute top-full left-0 right-0 animate-fade-in-up overflow-hidden"
                style={{
                  backdropFilter: mobBlur ? 'blur(12px)' : 'none',
                  WebkitBackdropFilter: mobBlur ? 'blur(12px)' : 'none',
                  borderBottom: navStyle.borderBottom !== false
                    ? `1px solid ${mobBg}30`
                    : 'none',
                  boxShadow: shadowMap[navStyle.shadow || 'sm'] || shadowMap.sm,
                }}
              >
                {/* Background layer with adjustable opacity */}
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundColor: mobBg,
                    opacity: mobOpacity,
                  }}
                />
                <nav className="relative z-10 flex flex-col p-4 space-y-4">
                  {sortedNavItems.map((item) => {
                    if (item.isButton) {
                      return (
                        <a 
                          key={item.id}
                          href={getNavHref(item)}
                          target={item.isExternal ? '_blank' : undefined}
                          rel={item.isExternal ? 'noopener noreferrer' : undefined}
                          className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all font-medium shadow-md"
                          style={{ backgroundColor: navStyle.buttonBgColor || '#2563eb', color: navStyle.buttonTextColor || '#ffffff' }}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {item.label}
                          {item.isExternal && <ExternalLink className="h-4 w-4" />}
                        </a>
                      );
                    }
                    return (
                      <a 
                        key={item.id}
                        href={getNavHref(item)}
                        target={item.isExternal ? '_blank' : undefined}
                        rel={item.isExternal ? 'noopener noreferrer' : undefined}
                        className="font-medium py-2 border-b hover:opacity-70 transition-colors"
                        style={{ color: navStyle.textColor || '#334155', borderColor: `${mobBg}20` }}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </a>
                    );
                  })}
                </nav>
              </div>
            );
          })()}
        </div>
      </header>
      )}

      {/* Dynamic blocks rendering based on CMS order */}
      {sortedBlocks.map(block => {
        // Skip global parallax block - it's rendered as page-wide bg above
        if (block.type === 'parallax' && block?.content?.globalParallax) return null;
        let rendered = renderBlock(block);
        const c = block?.content || {};

        // Apply user-defined spacing (padding / line-height / letter-spacing) to the block's root element
        const spacing = blockSpacingStyle(c);
        if (rendered && Object.keys(spacing).length > 0 && React.isValidElement(rendered)) {
          rendered = React.cloneElement(rendered, {
            style: { ...(rendered.props.style || {}), ...spacing },
          });
        }
        // Support both block-level (Obrázek tab) and content-level (parallax toggle) bg image
        const blockBgImage = block.backgroundImage || c.parallaxImage;
        const blockBgOpacity = (block.backgroundImageOpacity ?? c.parallaxImageOpacity ?? 100) / 100;
        const blockBgFixed = block.backgroundFixed || c.parallax;
        const blockBgPos = block.backgroundPosition || 'center';
        const blockBgSize = block.backgroundSize || 'cover';
        const blockOverlayColor = block.overlayColor || c.parallaxOverlayColor || '#000000';
        const blockOverlayOpacity = (block.overlayOpacity ?? c.parallaxOverlayOpacity ?? 0) / 100;
        
        // Skip wrapping for hero/parallax types (they have their own bg handling)
        if (blockBgImage && block.type !== 'hero' && block.type !== 'parallax') {
          return (
            <div key={block.id} className="relative">
              {/* Background image layer with opacity */}
              <div className="absolute inset-0 z-0" style={{
                backgroundImage: `url("${blockBgImage}")`,
                ...backgroundDisplayStyle(block, 'backgroundImage', deviceType, blockBgSize, blockBgPos),
                backgroundAttachment: blockBgFixed ? 'fixed' : 'scroll',
                backgroundRepeat: 'no-repeat',
                opacity: blockBgOpacity
              }} />
              {/* Overlay layer */}
              {blockOverlayOpacity > 0 && (
                <div className="absolute inset-0 z-[1]" style={{ backgroundColor: blockOverlayColor, opacity: blockOverlayOpacity }} />
              )}
              {/* Content */}
              <div className="relative z-[2]">{rendered}</div>
            </div>
          );
        }
        return <React.Fragment key={block.id}>{rendered}</React.Fragment>;
      })}

      {/* Floating contact button */}
      <FloatingContactButton config={cmsConfig?.settings?.floatingContact || {}} />

      {/* GDPR Cookie bar */}
      <CookieBar />
      </div>
    </div>
  );
};

export default HomePage;
